import { useEffect, useState, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useWorkspace } from '../contexts/WorkspaceContext';
import { useAuth } from '../contexts/AuthContext';
import { clientGroupAPI, clientPageAPI, taskAPI, workspaceAPI } from '../lib/api';
import { sortClientsAlpha } from '../lib/sortClients';
import { useLiveInvalidate } from '../lib/useLiveData';
// RD3 — Shared @dnd-kit components for accessible kanban.
import {
  DndContext, useDroppable, useDraggable, PointerSensor, KeyboardSensor,
  useSensor, useSensors,
} from '@dnd-kit/core';
import { NotionEditor } from '../components/NotionEditor';
import ShareWithControl from '../components/ShareWithControl';
import { AIEnhanceButton } from '../components/AIEnhanceButton';
import { TaskDetailSheet } from '../components/TaskDetailSheet';
import { TaskCreateModal } from '../components/TaskCreateModal';
import { DateRangeFilter } from '../components/DateRangeFilter';
import BrandKitTab from '../components/BrandKitTab';
import ClientMediaLibrary from '../components/ClientMediaLibrary';
import ClientLocationsTab from '../components/ClientLocationsTab';
import ContentCalendar from '../components/ContentCalendar';
import { formatDate } from '../lib/dateFormat';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { ScrollArea } from '../components/ui/scroll-area';
import { Separator } from '../components/ui/separator';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../components/ui/dropdown-menu';
import { StellarSelect } from '../components/StellarFormControls';
import { roleLabel, UserChip } from '../components/UserChip';
import { TaskStatusIndicator } from '../components/TaskStatusIndicator';
import { AutoGrowTextarea } from '../components/AutoGrowTextarea';
import { toast } from 'sonner';
import {
  Building2, Plus, FileText, ChevronRight, ArrowLeft, Trash2, Clock, Share2, Copy,
  ExternalLink, Loader2, Sparkles, MoreHorizontal, Edit3, X, Upload, Search, Download,
  CheckCircle2, Circle, AlertTriangle, Palette, Users, BarChart3, FolderOpen, Activity, MapPin, Calendar,
  LayoutGrid, List as ListIcon, GripVertical
} from 'lucide-react';

// D83 — Status pipeline metadata (single source of truth shared by the
// Board columns and the inline List-view status dropdown).
// 2026-02 — Relabel "Done" → "Archived" per Owner request. Underlying
// status enum stays `done` to avoid a backend migration; the user sees
// "Archived" everywhere in the status dropdown / pipeline columns.
const D83_STATUSES = [
  { id: 'ongoing',   label: 'Ongoing',   colorClass: 'text-emerald-300', badgeClass: 'bg-emerald-500/10 text-emerald-300 border-emerald-500/30', dotClass: 'bg-emerald-400' },
  { id: 'potential', label: 'Potential', colorClass: 'text-sky-300',     badgeClass: 'bg-sky-500/10 text-sky-300 border-sky-500/30',             dotClass: 'bg-sky-400' },
  { id: 'seasonal',  label: 'Seasonal',  colorClass: 'text-amber-300',   badgeClass: 'bg-amber-500/10 text-amber-300 border-amber-500/30',       dotClass: 'bg-amber-400' },
  { id: 'done',      label: 'Archived',  colorClass: 'text-muted-foreground/70', badgeClass: 'bg-foreground/[0.05] text-muted-foreground border-border/30', dotClass: 'bg-muted-foreground/50' },
];
const D83_STATUS_BY_ID = Object.fromEntries(D83_STATUSES.map(s => [s.id, s]));
function d83NormalizeStatus(s) {
  const k = (s || '').toLowerCase();
  return D83_STATUS_BY_ID[k] ? k : 'ongoing';
}

function ordinal(n) {
  if (!n && n !== 0) return '';
  const s = ['th', 'st', 'nd', 'rd'];
  const v = n % 100;
  return n + (s[(v - 20) % 10] || s[v] || s[0]);
}

const TABS = [
  { id: 'overview', label: 'Overview', icon: Building2 },
  { id: 'tasks', label: 'Tasks', icon: CheckCircle2 },
  { id: 'locations', label: 'Locations', icon: MapPin },
  { id: 'content', label: 'Content Calendar', icon: Calendar },
  { id: 'brand', label: 'Brand Kit', icon: Palette },
  { id: 'contacts', label: 'Contacts', icon: Users },
  { id: 'deliverables', label: 'Deliverables', icon: BarChart3 },
  { id: 'pages', label: 'Pages', icon: FileText },
  { id: 'files', label: 'Media', icon: FolderOpen },
  { id: 'activity', label: 'Activity', icon: Activity },
];

// RD3 — Shared droppable column wrapper for the client board kanban.
function ClientBoardDroppable({ id, className, children }) {
  const { setNodeRef, isOver } = useDroppable({ id });
  return (
    <div
      ref={setNodeRef}
      className={`${className} ${isOver ? 'ring-2 ring-gold/60 ring-offset-1 ring-offset-background' : ''}`}
      data-testid={`client-board-column-${id}`}
    >
      {children}
    </div>
  );
}

// RD3 — Draggable card with click-vs-drag (5px activation distance).
function ClientBoardDraggable({ id, disabled, onOpen, children, className }) {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({ id, disabled });
  const style = transform ? { transform: `translate3d(${transform.x}px, ${transform.y}px, 0)`, zIndex: 50 } : undefined;
  return (
    <div
      ref={setNodeRef}
      style={style}
      {...listeners}
      {...attributes}
      onClick={onOpen}
      data-testid={`client-board-card-${id}`}
      className={`${className} ${isDragging ? 'opacity-50' : ''} focus:outline-none focus:ring-2 focus:ring-gold/60`}
    >
      {children}
    </div>
  );
}

export default function MyClientsPage() {
  const { currentWorkspace, workspaceRole } = useWorkspace();
  const { user } = useAuth();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedClient, setSelectedClient] = useState(null);
  const [clientPages, setClientPages] = useState([]);
  const [clientTasks, setClientTasks] = useState([]);
  const [clientFiles, setClientFiles] = useState([]);
  // Batch 3 A5 — Per-cycle deliverable counts (fetched on client open)
  const [deliverableCounts, setDeliverableCounts] = useState(null);
  const [activePage, setActivePage] = useState(null);
  const [pageTitle, setPageTitle] = useState('');
  const [saving, setSaving] = useState(false);
  const [selectedTaskId, setSelectedTaskId] = useState(null);
  // RD3 — Kanban DnD sensors (pointer + keyboard). Defined once at the page
  // level so the hooks fire regardless of which tab is currently visible.
  const boardSensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor),
  );
  const [showAddClient, setShowAddClient] = useState(false);
  // Batch 2B B8 (CT-016) — inline Add Contact dialog state.
  const [showAddContact, setShowAddContact] = useState(false);
  const [newContact, setNewContact] = useState({ name: '', email: '', phone: '', role: 'Primary Contact' });
  const [showEditClient, setShowEditClient] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  const [taskView, setTaskView] = useState(() => typeof window !== 'undefined' ? localStorage.getItem('stellar_client_tasks_view') || 'list' : 'list');
  // Batch 1 ITEM 4 (BUG-RW-T-002 + CT-010) — encode client + tab in the URL.
  // /tasks/clients?client=<uuid>&tab=<overview|brand-kit|...>. Refresh-safe,
  // back-button works, deep-link shareable.
  const [searchParams, setSearchParams] = useSearchParams();
  const VALID_TABS = new Set(['overview', 'brand', 'contacts', 'deliverables', 'pages', 'files', 'activity', 'tasks', 'locations', 'content']);
  // Map UI tab id (kebab-case) ↔ internal tab id (snake_case where the
  // original component used them). Right now the component uses kebab-case
  // already (activeTab='brand-kit', etc.) so the map is identity.
  const TAB_PARAM_TO_INTERNAL = (t) => (VALID_TABS.has(t) ? t : 'overview');
  const [invalidClientId, setInvalidClientId] = useState(null);
  const [collapsedGroups, setCollapsedGroups] = useState({});
  const [deadlineRange, setDeadlineRange] = useState({ rangeId: 'week', start: '', end: '' });
  const [clientSidebarCollapsed, setClientSidebarCollapsed] = useState(() => {
    return typeof window !== 'undefined' && localStorage.getItem('stellar_client_sidebar_collapsed') === 'true';
  });
  const toggleClientSidebar = () => {
    const next = !clientSidebarCollapsed;
    setClientSidebarCollapsed(next);
    localStorage.setItem('stellar_client_sidebar_collapsed', String(next));
  };
  const [clientFilter, setClientFilter] = useState('all');
  // D83 — Board / List view toggle for the client pipeline.
  const [pipelineView, setPipelineView] = useState(() =>
    typeof window !== 'undefined'
      ? (localStorage.getItem('stellar_clients_view') === 'board' ? 'board' : 'list')
      : 'list'
  );
  const togglePipelineView = (mode) => {
    setPipelineView(mode);
    if (typeof window !== 'undefined') localStorage.setItem('stellar_clients_view', mode);
  };
  const [searchQuery, setSearchQuery] = useState('');
  const [members, setMembers] = useState([]);
  const [logoUrls, setLogoUrls] = useState({});
  // D60 PHASE 1 — Location counts per client for the "(N locations)" badge.
  const [locationCounts, setLocationCounts] = useState({});
  const [newClient, setNewClient] = useState({ name: '', description: '', contacts: [{ name: '', email: '', phone: '' }], deliverables: { posts: 0, stories: 0, reels: 0 }, posting_cycle: { start_day: 1, end_day: 1 }, custom_deliverable_types: [], brand_colors: [''], brand_fonts: [''], brand_drive_link: '' });
  const [addingClient, setAddingClient] = useState(false);
  // Iter 62 Bug #2 / Feature #8 / #9: New Task modal scoped to the client tab.
  // Pre-fills client_group_id; assignees default to current user; multi-select.
  const [showNewTask, setShowNewTask] = useState(false);
  // BUG-C05 (2026-05-21) — Delete Client confirmation dialog state.
  // Replaces the broken native `confirm()` call which (a) didn't render
  // reliably from inside a Radix DropdownMenuItem onClick, (b) showed no
  // cascade impact, (c) had no Undo. Toast Undo posts to the new
  // /client-groups/{ws}/{id}/restore endpoint within 5s.
  const [deleteDialog, setDeleteDialog] = useState(null); // { client, counts, loading }
  const [deletingClient, setDeletingClient] = useState(false);
  const logoFileRef = useRef(null);
  const fileUploadRef = useRef(null);
  const saveTimerRef = useRef(null);

  const wsId = currentWorkspace?.id;
  // Iter 71 — combine global user.role + workspaceRole so a Team user with
  // an accidentally elevated user.role can't bypass workspace-level gating.
  const userRole = user?.role || 'team';
  const wsRole = workspaceRole || userRole;
  const isOwnerOrAdmin =
    ['super_admin', 'owner', 'admin'].includes(userRole) &&
    ['owner', 'admin', 'super_admin'].includes(wsRole);
  const isManager = wsRole === 'manager';
  const isTeam = wsRole === 'team' || userRole === 'team';

  // Permissions for current client
  const isAccountMgr = selectedClient ? (selectedClient.account_managers || []).includes(user?.id) : false;
  const canEdit = isOwnerOrAdmin || isAccountMgr;
  const canDelete = isOwnerOrAdmin;
  const canChangeMgrs = isOwnerOrAdmin || (isManager && isAccountMgr);

  // D84 — Live load helper. Extracted from the useEffect so we can ALSO
  // call it when the workspace WebSocket pushes a `clients` / `pipeline`
  // invalidate (another teammate moved a card / archived a client / …).
  const loadClientsLive = useCallback(() => {
    if (!wsId) return;
    const includeArchived = clientFilter === 'archived';
    const managedByMe = clientFilter === 'mine';
    clientGroupAPI.list(wsId, { includeArchived, managedByMe })
      .then(res => setClients(res.data)).catch(() => {});
    clientGroupAPI.locationCounts(wsId).then(res => setLocationCounts(res.data || {})).catch(() => {});
  }, [wsId, clientFilter]);

  useEffect(() => {
    if (!wsId) return;
    setLoading(true);
    // Batch 2A CT-008/009 — when the user picks the "Archived" filter we
    // need archived rows from the backend; otherwise default (excludes them).
    const includeArchived = clientFilter === 'archived';
    // Batch 5 R2 F1 — when on the "Clients I Manage" tab, ask the server
    // for the hybrid-filtered set so Admin/Manager don't fall back to
    // "show everything".
    const managedByMe = clientFilter === 'mine';
    clientGroupAPI.list(wsId, { includeArchived, managedByMe }).then(res => setClients(res.data)).catch(() => {}).finally(() => setLoading(false));
    workspaceAPI.listMembers(wsId).then(res => setMembers((res.data || []).map(m => m.user ? { ...m.user, role: m.role } : null).filter(Boolean))).catch(() => {});
    // D60 PHASE 1 — Fetch location counts for the "(N locations)" badge.
    clientGroupAPI.locationCounts(wsId).then(res => setLocationCounts(res.data || {})).catch(() => {});
  }, [wsId, clientFilter]);

  // D84 — Real-time push subscriptions. When ANY teammate adds / renames /
  // moves / archives a client OR drags a card between D83 pipeline columns,
  // the workspace WS fires an `invalidate` and we silently refresh the
  // list + location counts. Falls back to the existing 15s polling cadence
  // (inherited from the parent useEffect cleanup) when the WS isn't live.
  useLiveInvalidate('clients', loadClientsLive);
  useLiveInvalidate('pipeline', loadClientsLive);

  // D60 PHASE 1 — Live refresh of location counts when any tab broadcasts
  // a `stellar-client-locations-changed` event (e.g. user added a location
  // in another browser tab). Keeps the "(N locations)" sidebar badge fresh.
  useEffect(() => {
    if (!wsId) return undefined;
    const handler = () => {
      clientGroupAPI.locationCounts(wsId).then(res => setLocationCounts(res.data || {})).catch(() => {});
    };
    window.addEventListener('stellar-client-locations-changed', handler);
    return () => window.removeEventListener('stellar-client-locations-changed', handler);
  }, [wsId]);

  // Fetch logo URLs for all clients (try each, cache result)
  useEffect(() => {
    if (!wsId) return;
    clients.forEach(c => {
      if (logoUrls[c.id]) return; // Already cached
      fetch(`${process.env.REACT_APP_BACKEND_URL}/api/client-groups/${wsId}/${c.id}/logo`)
        .then(r => { if (r.ok) return r.blob(); return null; })
        .then(blob => { if (blob && blob.size > 0) setLogoUrls(prev => ({ ...prev, [c.id]: URL.createObjectURL(blob) })); })
        .catch(() => {});
    });
  }, [clients, wsId]);

  const openClient = async (client) => {
    setSelectedClient(client);
    setActiveTab('overview');
    setActivePage(null);
    setDeliverableCounts(null);
    setInvalidClientId(null);
    // Batch 1 ITEM 4 — write ?client=<uuid>&tab=overview to the URL.
    setSearchParams({ client: client.id, tab: 'overview' }, { replace: false });
    if (!wsId) return;
    clientPageAPI.list(wsId, client.id).then(res => setClientPages(res.data)).catch(() => {});
    taskAPI.list(wsId, { client_group_id: client.id }).then(res => setClientTasks(res.data)).catch(() => {});
    clientGroupAPI.listFiles(wsId, client.id).then(res => setClientFiles(res.data || [])).catch(() => setClientFiles([]));
    clientGroupAPI.deliverableCounts(wsId, client.id).then(res => setDeliverableCounts(res.data)).catch(() => setDeliverableCounts(null));
  };

  // Batch 1 ITEM 4 — wrap setActiveTab to also push the new tab into the URL.
  const setActiveTabPersist = (tab) => {
    setActiveTab(tab);
    if (selectedClient) {
      setSearchParams({ client: selectedClient.id, tab }, { replace: false });
    }
  };

  // Batch 1 ITEM 4 — on mount / URL change, restore client + tab from params.
  // Handles direct nav, refresh, and back-button.
  useEffect(() => {
    const clientId = searchParams.get('client');
    const tabParam = searchParams.get('tab');
    if (!clientId) {
      // No client in URL: leave panel closed (default list view).
      setSelectedClient(prev => prev || null);
      return;
    }
    if (selectedClient && selectedClient.id === clientId) {
      // Already on this client — just sync the tab if it changed.
      const tab = TAB_PARAM_TO_INTERNAL(tabParam || 'overview');
      if (tab !== activeTab) setActiveTab(tab);
      return;
    }
    if (!wsId || clients.length === 0) return;
    const found = clients.find(c => c.id === clientId);
    if (!found) {
      // Invalid / no-access UUID — surface a friendly error instead of dumping
      // the user back to the list with no explanation.
      setInvalidClientId(clientId);
      setSelectedClient(null);
      return;
    }
    setInvalidClientId(null);
    setSelectedClient(found);
    const tab = TAB_PARAM_TO_INTERNAL(tabParam || 'overview');
    setActiveTab(tab);
    setActivePage(null);
    setDeliverableCounts(null);
    clientPageAPI.list(wsId, found.id).then(res => setClientPages(res.data)).catch(() => {});
    taskAPI.list(wsId, { client_group_id: found.id }).then(res => setClientTasks(res.data)).catch(() => {});
    clientGroupAPI.listFiles(wsId, found.id).then(res => setClientFiles(res.data || [])).catch(() => setClientFiles([]));
    clientGroupAPI.deliverableCounts(wsId, found.id).then(res => setDeliverableCounts(res.data)).catch(() => setDeliverableCounts(null));
  }, [searchParams, wsId, clients]); // eslint-disable-line react-hooks/exhaustive-deps

  const refreshClient = async () => {
    if (!wsId || !selectedClient) return;
    const res = await clientGroupAPI.list(wsId);
    const updated = (res.data || []).find(c => c.id === selectedClient.id);
    if (updated) { setSelectedClient(updated); setClients(res.data); }
  };

  // BUG-C05 — open the cascade-confirm dialog. Loads counts from a
  // dedicated read-only preview endpoint (zero side effects). Showing
  // counts beats forcing the user to discover them by visiting tabs.
  //
  // Hardening (post-Bundle 4A prod 502 incident, 2026-05-21):
  // If the preview endpoint fails (network blip / 5xx / 502), we DO NOT
  // close the dialog. Instead we mark it as preview-failed and let the
  // user still confirm — the actual DELETE call has its own error toast.
  // This decouples the preview's availability from the user's ability to
  // delete; otherwise a flaky backend makes the entire Delete UX unusable.
  const openDeleteDialog = async (client) => {
    setDeleteDialog({ client, counts: null, loading: true, previewError: null });
    try {
      const res = await clientGroupAPI.deletePreview(wsId, client.id);
      setDeleteDialog({ client, counts: res.data, loading: false, previewError: null });
    } catch (err) {
      const msg = typeof err?.response?.data?.detail === 'string' ? err.response.data.detail : (err?.message || 'Could not load delete preview');
      // Keep the dialog open with a soft error banner instead of dropping it.
      setDeleteDialog({ client, counts: null, loading: false, previewError: msg });
    }
  };

  // BUG-C05 — perform the soft-delete, then surface a sonner toast with
  // an Undo action that calls /restore within 5s. The list is updated
  // optimistically so the client disappears immediately.
  const confirmDeleteClient = async () => {
    if (!deleteDialog?.client) return;
    const client = deleteDialog.client;
    setDeletingClient(true);
    try {
      await clientGroupAPI.delete(wsId, client.id);
      setClients(prev => prev.filter(c => c.id !== client.id));
      if (selectedClient?.id === client.id) setSelectedClient(null);
      setDeleteDialog(null);
      toast.success(`"${client.name}" deleted`, {
        duration: 5000,
        action: {
          label: 'Undo',
          onClick: async () => {
            try {
              await clientGroupAPI.restore(wsId, client.id);
              const res = await clientGroupAPI.list(wsId);
              setClients(res.data || []);
              toast.success(`"${client.name}" restored`);
            } catch (e) {
              const msg = typeof e?.response?.data?.detail === 'string' ? e.response.data.detail : (e?.message || 'Could not restore');
              toast.error(msg);
            }
          },
        },
      });
    } catch (err) {
      const msg = typeof err?.response?.data?.detail === 'string' ? err.response.data.detail : (err?.message || 'Could not delete client');
      toast.error(msg);
    } finally {
      setDeletingClient(false);
    }
  };

  const handleAddClient = async () => {
    if (!newClient.name.trim()) return toast.error('Business name is required');
    setAddingClient(true);
    try {
      await clientGroupAPI.create({ workspace_id: wsId, ...newClient, contacts: newClient.contacts.filter(c => c.name || c.email || c.phone), brand_colors: newClient.brand_colors.filter(c => c), brand_fonts: newClient.brand_fonts.filter(f => f) });
      toast.success('Client added');
      setShowAddClient(false);
      setNewClient({ name: '', description: '', contacts: [{ name: '', email: '', phone: '' }], deliverables: { posts: 0, stories: 0, reels: 0 }, posting_cycle: { start_day: 1, end_day: 1 }, custom_deliverable_types: [], brand_colors: [''], brand_fonts: [''], brand_drive_link: '' });
      clientGroupAPI.list(wsId).then(res => setClients(res.data)).catch(() => {});
    } catch (err) { toast.error(typeof err?.response?.data?.detail === 'string' ? err.response.data.detail : 'Operation failed'); }
    setAddingClient(false);
  };

  const handleSaveEdit = async () => {
    try {
      const res = await clientGroupAPI.update(wsId, selectedClient.id, { name: newClient.name, description: newClient.description, contacts: newClient.contacts.filter(c => c.name || c.email || c.phone), deliverables: newClient.deliverables, posting_cycle: newClient.posting_cycle, custom_deliverable_types: newClient.custom_deliverable_types || [], brand_colors: newClient.brand_colors?.filter(c => c), brand_fonts: newClient.brand_fonts?.filter(f => f), brand_drive_link: newClient.brand_drive_link });
      setSelectedClient(res.data);
      setClients(prev => prev.map(c => c.id === selectedClient.id ? res.data : c));
      setShowEditClient(false);
      toast.success('Client updated');
    } catch (err) { toast.error(typeof err?.response?.data?.detail === 'string' ? err.response.data.detail : 'Operation failed'); }
  };

  const openEditDialog = () => {
    setNewClient({ name: selectedClient.name || '', description: selectedClient.description || '', contacts: selectedClient.contacts?.length ? selectedClient.contacts : [{ name: '', email: '', phone: '' }], deliverables: selectedClient.deliverables || { posts: 0, stories: 0, reels: 0 }, posting_cycle: selectedClient.posting_cycle || { start_day: 1, end_day: 1 }, custom_deliverable_types: selectedClient.custom_deliverable_types || [], brand_colors: selectedClient.brand_colors?.length ? selectedClient.brand_colors : [''], brand_fonts: selectedClient.brand_fonts?.length ? selectedClient.brand_fonts : [''], brand_drive_link: selectedClient.brand_drive_link || '' });
    setShowEditClient(true);
  };

  const addManager = async (uid) => {
    const newMgrs = [...(selectedClient.account_managers || []), uid];
    try {
      const res = await clientGroupAPI.update(wsId, selectedClient.id, { account_managers: newMgrs });
      setSelectedClient(res.data); setClients(prev => prev.map(c => c.id === selectedClient.id ? res.data : c));
      toast.success('Account manager added');
    } catch (err) { toast.error(typeof err?.response?.data?.detail === 'string' ? err.response.data.detail : 'Operation failed'); }
  };

  const removeManager = async (uid) => {
    const newMgrs = (selectedClient.account_managers || []).filter(id => id !== uid);
    try {
      const res = await clientGroupAPI.update(wsId, selectedClient.id, { account_managers: newMgrs });
      setSelectedClient(res.data); setClients(prev => prev.map(c => c.id === selectedClient.id ? res.data : c));
      toast.success('Account manager removed');
    } catch (err) { toast.error(typeof err?.response?.data?.detail === 'string' ? err.response.data.detail : 'Operation failed'); }
  };

  const uploadLogo = async (file) => {
    try {
      await clientGroupAPI.uploadLogo(wsId, selectedClient.id, file);
      const blobUrl = URL.createObjectURL(file);
      setLogoUrls(prev => ({ ...prev, [selectedClient.id]: blobUrl }));
      const res = await clientGroupAPI.list(wsId);
      const updated = (res.data || []).find(c => c.id === selectedClient.id);
      if (updated) { setSelectedClient(updated); setClients(res.data); }
      toast.success('Logo uploaded');
    } catch (err) {
      const msg = err?.response?.data?.detail;
      toast.error(typeof msg === 'string' ? msg : 'Logo upload failed. Please try again.');
    }
  };

  const createClientPage = async () => {
    if (!wsId || !selectedClient) return;
    try {
      const res = await clientPageAPI.create({ workspace_id: wsId, client_group_id: selectedClient.id, title: 'Untitled' });
      setClientPages(prev => [...prev, res.data]);
      // Batch 2B B13 (CT-023) — open the editor immediately on create so the
      // user doesn't have to click the new page to start writing.
      setActivePage(res.data);
      setPageTitle(res.data.title || '');
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = typeof detail === 'string' ? detail : (Array.isArray(detail) ? detail.map(d => d.msg).join(', ') : 'Failed to create page');
      toast.error(msg);
    }
  };

  const autoSave = (field, value) => {
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    setSaving(true);
    saveTimerRef.current = setTimeout(async () => {
      try {
        await clientPageAPI.update(wsId, activePage.id, { [field]: value });
        setClientPages(prev => prev.map(p => p.id === activePage.id ? { ...p, [field]: value } : p));
      } catch {}
      setSaving(false);
    }, 1000);
  };

  const filteredClients = clients
    // Batch 5 R2 F1 — "mine" tab now uses the server-side hybrid filter
    // (?managed_by_me=true). No more client-side fallback for Admin/Owner;
    // the server returns the correct set based on account_managers +
    // supervisor tree.
    .filter(c => {
      if (clientFilter === 'archived') return !!c.archived_at;
      // 'mine' or 'all' — hide archived
      return !c.archived_at;
    })
    .filter(c => !searchQuery || c.name?.toLowerCase().includes(searchQuery.toLowerCase()));

  // D83 — Group filtered clients by their pipeline status. Each column is
  // sorted ALPHABETICALLY (A→Z, locale-aware, natural-numeric). Used by both
  // the Board view AND the new grouped List view. 2026-02 — replaced the
  // earlier pipeline_order sort with the workspace-wide alphabetical sort
  // per Owner spec ("Sort alphabetically A → Z within each group").
  const clientsByStatus = (() => {
    const buckets = { ongoing: [], potential: [], seasonal: [], done: [] };
    for (const c of filteredClients) {
      buckets[d83NormalizeStatus(c.status)].push(c);
    }
    for (const k of Object.keys(buckets)) {
      buckets[k] = sortClientsAlpha(buckets[k]);
    }
    return buckets;
  })();

  // D83 — Persist a single client status (+optional order) to the backend.
  // Optimistic UI: update local state first, revert on failure.
  // Used both by the inline List-view dropdown and the Board drag handler.
  const applyPipelineUpdate = useCallback(async (clientId, nextStatus, nextOrder = null) => {
    if (!wsId) return;
    const status = d83NormalizeStatus(nextStatus);
    const prevSnapshot = clients;
    // Optimistic local patch.
    setClients(prev => prev.map(c => c.id === clientId
      ? { ...c, status, ...(nextOrder !== null ? { pipeline_order: nextOrder } : {}) }
      : c));
    try {
      const res = await clientGroupAPI.updatePipeline(wsId, clientId, status, nextOrder);
      // Reconcile with server's authoritative order (in case server appended).
      const serverOrder = res?.data?.pipeline_order;
      if (typeof serverOrder === 'number') {
        setClients(prev => prev.map(c => c.id === clientId ? { ...c, status, pipeline_order: serverOrder } : c));
      }
    } catch (err) {
      setClients(prevSnapshot);
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Failed to update status — reverted.');
    }
  }, [wsId, clients]);

  // D83 — DnD end handler for the Board view. Computes a fractional
  // `pipeline_order` for the dropped card (or appends to the bottom of
  // the column) and POSTS via applyPipelineUpdate. dnd-kit gives us
  // `over.id` = target column id OR a card id; we treat card drops as
  // "drop above this card" by using the dragged card's destination index
  // inside the column to compute order = (prev+next)/2.
  const handleBoardDragEnd = useCallback((event) => {
    const { active, over } = event;
    if (!over) return;
    const draggedId = active.id;
    const overId = over.id;
    // Decide target column. dnd-kit's `over` can be either a column
    // droppable (id = status) or another card (id = clientId).
    const isColumnTarget = D83_STATUS_BY_ID[overId];
    const targetStatus = isColumnTarget ? overId : d83NormalizeStatus(
      (clients.find(c => c.id === overId) || {}).status
    );
    const draggedClient = clients.find(c => c.id === draggedId);
    if (!draggedClient) return;
    const fromStatus = d83NormalizeStatus(draggedClient.status);
    const targetColumn = clientsByStatus[targetStatus].filter(c => c.id !== draggedId);
    let newOrder = null;
    if (!isColumnTarget && overId !== draggedId) {
      // Dropped on a card → place ABOVE that card.
      const idx = targetColumn.findIndex(c => c.id === overId);
      if (idx >= 0) {
        const prev = idx === 0 ? null : targetColumn[idx - 1];
        const next = targetColumn[idx];
        const prevOrder = prev?.pipeline_order ?? (next?.pipeline_order ?? 1000) - 2000;
        const nextOrder = next?.pipeline_order ?? prevOrder + 2000;
        newOrder = (prevOrder + nextOrder) / 2;
      }
    }
    // No-op: dropped onto own column with no card target AND status unchanged.
    if (fromStatus === targetStatus && newOrder === null && isColumnTarget) return;
    applyPipelineUpdate(draggedId, targetStatus, newOrder);
  }, [clients, clientsByStatus, applyPipelineUpdate]);

  const acctMgrs = selectedClient ? (selectedClient.account_managers || []).map(id => members.find(m => m.id === id)).filter(Boolean) : [];
  const deliverables = selectedClient?.deliverables || {};
  const completedThisMonth = clientTasks.filter(t => t.status === 'completed').length;
  const monthlyTarget = (deliverables.posts || 0) + (deliverables.stories || 0) + (deliverables.reels || 0);
  const progress = monthlyTarget > 0 ? Math.min(100, Math.round((completedThisMonth / monthlyTarget) * 100)) : 0;
  const activeTasks = clientTasks.filter(t => t.status !== 'completed' && t.status !== 'cancelled').length;
  const overdueTasks = clientTasks.filter(t => t.due_date && formatDate(t.due_date) < new Date().toISOString().slice(0, 10) && t.status !== 'completed').length;
  const logoUrl = selectedClient ? logoUrls[selectedClient.id] : null;

  // ============ CLIENT DETAIL PAGE (8-tab ClickUp layout) ============
  if (selectedClient) {
    // Page editor overlay
    if (activePage) {
      const isCreator = activePage.created_by === user?.id;
      return (
        <div className="h-full overflow-y-auto bg-background">
          <div className="max-w-3xl mx-auto py-8 px-6">
            <button onClick={() => setActivePage(null)} className="flex items-center gap-1.5 text-[12px] text-muted-foreground hover:text-foreground mb-4"><ArrowLeft className="h-3.5 w-3.5" /> Back to {selectedClient.name}</button>
            <div className="flex items-center justify-between mb-4"><div className="flex items-center gap-2 text-[11px] text-muted-foreground/40">{saving && <><Loader2 className="h-3 w-3 animate-spin" /> Saving...</>}</div></div>
            <input value={pageTitle} onChange={e => { setPageTitle(e.target.value); autoSave('title', e.target.value); }} placeholder="Untitled" className="w-full text-[32px] font-display font-bold bg-transparent border-none outline-none placeholder:text-muted-foreground/20 mb-4" />
            {/* D74 — Share-with control. Creator-only. Empty list = team-default (everyone with client access). */}
            {isCreator && (
              <div className="mb-4" data-testid={`client-page-share-bar-${activePage.id}`}>
                <ShareWithControl
                  workspaceId={wsId}
                  noteId={activePage.id}
                  value={activePage.shared_with || []}
                  excludeUserId={user?.id}
                  emptyLabel="Team-visible (everyone with access to this client can see it)"
                  onChange={async (newIds) => {
                    const res = await clientPageAPI.setShare(wsId, activePage.id, newIds);
                    setActivePage({ ...activePage, shared_with: res.data.shared_with });
                    setClientPages(prev => prev.map(p => p.id === activePage.id ? { ...p, shared_with: res.data.shared_with } : p));
                  }}
                />
              </div>
            )}
            <NotionEditor
              key={activePage.id}
              content={activePage.content}
              onUpdate={(content) => autoSave('content', content)}
              placeholder="Type '/' for blocks..."
              workspaceId={currentWorkspace?.id}
              onMention={(m) => {
                // D80 Tier 2 — in-app notification only. NO emails.
                try {
                  const token = localStorage.getItem('stellar_token');
                  fetch(`${process.env.REACT_APP_BACKEND_URL}/api/notes/mention-notify`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
                    body: JSON.stringify({
                      workspace_id: currentWorkspace?.id,
                      mentioned_user_id: m.id,
                      note_id: activePage.id,
                      note_title: activePage.title || 'a client page',
                      surface: 'client_page',
                    }),
                  }).catch(() => {});
                } catch {}
              }}
            />
          </div>
        </div>
      );
    }

    return (
      <div className="h-full flex flex-col" data-testid="client-detail">
        {/* HEADER */}
        <div className="border-b border-border/30 px-6 py-4 shrink-0">
          <div className="max-w-5xl mx-auto">
            <button onClick={() => { setSelectedClient(null); setActivePage(null); setShowEditClient(false); setSearchParams({}, { replace: false }); }} className="flex items-center gap-1.5 text-[11px] text-muted-foreground hover:text-foreground mb-3"><ArrowLeft className="h-3 w-3" /> All Clients</button>
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-4">
                <div className="relative group">
                  {logoUrl ? <img src={logoUrl} alt="" className="h-14 w-14 rounded-xl object-cover" /> : <div className="h-14 w-14 rounded-xl bg-gold/10 flex items-center justify-center"><span className="text-xl font-bold text-gold">{(selectedClient.name || '?')[0]?.toUpperCase()}</span></div>}
                  {canEdit && <button onClick={() => logoFileRef.current?.click()} className="absolute inset-0 rounded-xl bg-black/50 opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity"><Upload className="h-4 w-4 text-white" /></button>}
                  <input ref={logoFileRef} type="file" accept="image/*" className="hidden" onChange={e => { if (e.target.files[0]) uploadLogo(e.target.files[0]); }} />
                </div>
                <div>
                  <h1 className="text-[20px] font-display font-bold">{selectedClient.name}</h1>
                  <div className="flex items-center gap-3 mt-0.5 text-[11px] text-muted-foreground/50">
                    {acctMgrs.length > 0 && <span>Managed by {acctMgrs.map(m => m.name).join(', ')}</span>}
                    <span>Client since {formatDate(selectedClient.created_at)}</span>
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {canEdit && <Button variant="outline" size="sm" className="h-8 text-[12px] border-border/30" onClick={openEditDialog} data-testid="edit-client-btn"><Edit3 className="h-3 w-3 mr-1.5" /> Edit</Button>}
                {canDelete && (
                  <DropdownMenu><DropdownMenuTrigger asChild><Button variant="ghost" size="icon" className="h-8 w-8" data-testid="client-kebab-btn"><MoreHorizontal className="h-4 w-4" /></Button></DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      {/* Batch 2A CT-008/009 — archive / unarchive */}
                      <DropdownMenuItem
                        className="text-[12px]"
                        data-testid={selectedClient?.archived_at ? 'client-unarchive-menu-item' : 'client-archive-menu-item'}
                        onSelect={async (e) => {
                          e.preventDefault();
                          try {
                            if (selectedClient?.archived_at) {
                              await clientGroupAPI.unarchive(wsId, selectedClient.id);
                              toast.success(`"${selectedClient.name}" unarchived`);
                            } else {
                              await clientGroupAPI.archive(wsId, selectedClient.id);
                              toast.success(`"${selectedClient.name}" archived`);
                            }
                            const res = await clientGroupAPI.list(wsId, { includeArchived: clientFilter === 'archived' });
                            setClients(res.data);
                            // Refresh selected client to pick up archived_at change
                            const updated = (res.data || []).find(c => c.id === selectedClient.id);
                            if (updated) setSelectedClient(updated);
                            else { setSelectedClient(null); setSearchParams({}, { replace: false }); }
                          } catch (err) {
                            toast.error(err?.response?.data?.detail || 'Action failed');
                          }
                        }}
                      >
                        <FolderOpen className="h-3 w-3 mr-2" />
                        {selectedClient?.archived_at ? 'Unarchive Client' : 'Archive Client'}
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        className="text-destructive text-[12px]"
                        data-testid="client-delete-menu-item"
                        onSelect={(e) => { e.preventDefault(); openDeleteDialog(selectedClient); }}
                      ><Trash2 className="h-3 w-3 mr-2" /> Delete Client</DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                )}
              </div>
            </div>
            {/* Stats row */}
            <div className="flex items-center gap-6 mt-3 text-[12px]">
              <span className="text-muted-foreground/50">Active Tasks: <strong className="text-foreground">{activeTasks}</strong></span>
              <span className="text-muted-foreground/50">Completed This Month: <strong className="text-foreground">{completedThisMonth}</strong></span>
              {monthlyTarget > 0 && <span className="text-muted-foreground/50">Deliverables: <strong className="text-gold">{completedThisMonth} of {monthlyTarget} this cycle</strong></span>}
              {monthlyTarget > 0 && <span className="text-muted-foreground/50">Progress: <strong className="text-gold">{progress}%</strong></span>}
              {overdueTasks > 0 && <span className="text-red-400"><strong>{overdueTasks}</strong> Overdue</span>}
            </div>
          </div>
        </div>

        {/* TAB BAR + CONTENT */}
        <div className="flex-1 flex overflow-hidden">
          {/* Tab sidebar — collapsible */}
          <div className={`${clientSidebarCollapsed ? 'w-[48px]' : 'w-[180px]'} border-r border-border/20 py-3 shrink-0 transition-all duration-200 flex flex-col`}>
            <button onClick={toggleClientSidebar} className="flex items-center justify-center px-2 py-1 mb-2 text-muted-foreground/30 hover:text-muted-foreground/60">
              <ChevronRight className={`h-3 w-3 transition-transform ${clientSidebarCollapsed ? '' : 'rotate-180'}`} />
            </button>
            {TABS.map(tab => (
              <button key={tab.id} onClick={() => setActiveTabPersist(tab.id)} data-testid={`client-tab-${tab.id}`} className={`flex items-center gap-2.5 w-full ${clientSidebarCollapsed ? 'justify-center px-1 py-2' : 'px-4 py-2'} text-[12px] transition-colors ${activeTab === tab.id ? 'text-gold bg-gold/5 border-r-2 border-gold' : 'text-muted-foreground/50 hover:text-foreground hover:bg-foreground/[0.02]'}`} title={clientSidebarCollapsed ? tab.label : undefined}>
                <tab.icon className="h-3.5 w-3.5 shrink-0" />
                {!clientSidebarCollapsed && <span>{tab.label}</span>}
              </button>
            ))}
          </div>

          {/* Tab content */}
          <ScrollArea className="flex-1 p-6">
            <div className="max-w-4xl">

              {/* OVERVIEW TAB */}
              {activeTab === 'overview' && (
                <div className="space-y-6">
                  {selectedClient.description && <div><h3 className="text-[10px] font-semibold text-muted-foreground/30 uppercase tracking-wider mb-2">About</h3><p className="text-[15px] text-foreground font-medium leading-relaxed">{selectedClient.description}</p></div>}
                  {(selectedClient.contacts || []).length > 0 && (
                    <div><h3 className="text-[10px] font-semibold text-muted-foreground/30 uppercase tracking-wider mb-2">Key Contacts</h3>
                    {selectedClient.contacts.slice(0, 3).map((c, i) => <div key={i} className="flex items-center gap-4 py-2 text-[14px]">{c.name && <span className="font-semibold text-foreground">{c.name}</span>}{c.email && <a href={`mailto:${c.email}`} className="text-gold hover:underline text-[13px]">{c.email}</a>}{c.phone && <span className="text-muted-foreground/50 text-[13px]">{c.phone}</span>}</div>)}
                    {selectedClient.contacts.length > 3 && <button onClick={() => setActiveTabPersist('contacts')} className="text-[12px] text-gold hover:underline mt-1">View all {selectedClient.contacts.length} contacts →</button>}
                    </div>
                  )}
                  <div><h3 className="text-[10px] font-semibold text-muted-foreground/30 uppercase tracking-wider mb-2">Account Managers</h3>
                    {acctMgrs.length > 0 ? acctMgrs.map(m => (
                      <div key={m.id} className="flex items-center gap-3 py-2" data-testid={`account-mgr-${m.id}`}>
                        <div className="h-8 w-8 rounded-full bg-gold/10 flex items-center justify-center"><span className="text-[11px] font-bold text-gold">{m.name?.[0]?.toUpperCase()}</span></div>
                        <div>
                          {/* Iter 65a — UserChip rendering site #1 (account managers list) */}
                          <UserChip user={m} showRole size="md" testId={`account-mgr-name-${m.id}`} className="font-semibold" />
                          <p className="text-[11px] text-muted-foreground/40">{m.email}</p>
                        </div>
                        {canChangeMgrs && <button onClick={() => removeManager(m.id)} className="ml-auto p-1 text-muted-foreground/20 hover:text-red-400"><X className="h-3 w-3" /></button>}
                      </div>
                    )) : <p className="text-[13px] text-muted-foreground/40">No managers assigned</p>}
                    {canChangeMgrs && (
                      <div className="mt-3 p-3 rounded-lg border border-border/20 bg-foreground/[0.02]">
                        <StellarSelect
                          value=""
                          onValueChange={(v) => { if (v) addManager(v); }}
                          options={members.filter(m => !(selectedClient.account_managers || []).includes(m.id)).map(m => ({ value: m.id, label: m.role ? `${m.name} (${roleLabel(m.role)})` : m.name }))}
                          placeholder="+ Add account manager..."
                          testId="add-manager-select"
                        />
                      </div>
                    )}
                  </div>
                  {clientTasks.filter(t => t.due_date && t.status !== 'completed').length > 0 && (
                    <div><h3 className="text-[10px] font-semibold text-muted-foreground/30 uppercase tracking-wider mb-2">Upcoming Deadlines</h3>
                    <DateRangeFilter value={deadlineRange} onChange={setDeadlineRange} storageKey="client_deadlines" className="mb-3" />
                    {(() => {
                      const filtered = clientTasks.filter(t => t.due_date && t.status !== 'completed').filter(t => {
                        if (!deadlineRange.start || !deadlineRange.end) return true;
                        const d = t.due_date.slice(0,10);
                        return d >= deadlineRange.start && d <= deadlineRange.end;
                      }).sort((a, b) => (a.due_date || '').localeCompare(b.due_date || ''));
                      return filtered.length > 0 ? filtered.slice(0, 10).map(t => <div key={t.id} className="flex items-center gap-3 py-1.5"><Circle className="h-2.5 w-2.5 text-gold/40" /><span className="flex-1 truncate text-[14px] text-foreground font-medium">{t.title}</span><span className="text-muted-foreground/40 text-[12px]">{formatDate(t.due_date)}</span></div>) : <p className="text-[13px] text-muted-foreground/40 py-3">No deadlines in this range</p>;
                    })()}
                    </div>
                  )}
                </div>
              )}

              {/* TASKS TAB */}
              {activeTab === 'tasks' && (() => {
                const setTV = (v) => { setTaskView(v); localStorage.setItem('stellar_client_tasks_view', v); };
                const today = new Date().toISOString().slice(0, 10);
                const tomorrow = new Date(Date.now() + 86400000).toISOString().slice(0, 10);
                const weekEnd = new Date(Date.now() + 7 * 86400000).toISOString().slice(0, 10);
                const nextWeekEnd = new Date(Date.now() + 14 * 86400000).toISOString().slice(0, 10);
                const groups = [
                  { id: 'overdue', label: 'Overdue', color: 'text-red-400', tasks: clientTasks.filter(t => t.due_date && t.due_date.slice(0,10) < today && t.status !== 'completed' && t.status !== 'cancelled') },
                  { id: 'today', label: 'Today', color: 'text-gold', tasks: clientTasks.filter(t => t.due_date?.slice(0,10) === today && t.status !== 'completed') },
                  { id: 'tomorrow', label: 'Tomorrow', color: 'text-foreground', tasks: clientTasks.filter(t => t.due_date?.slice(0,10) === tomorrow && t.status !== 'completed') },
                  { id: 'this_week', label: 'This Week', color: 'text-foreground', tasks: clientTasks.filter(t => t.due_date && t.due_date.slice(0,10) > tomorrow && t.due_date.slice(0,10) <= weekEnd && t.status !== 'completed') },
                  { id: 'next_week', label: 'Next Week', color: 'text-foreground', tasks: clientTasks.filter(t => t.due_date && t.due_date.slice(0,10) > weekEnd && t.due_date.slice(0,10) <= nextWeekEnd && t.status !== 'completed') },
                  { id: 'later', label: 'Later', color: 'text-muted-foreground/60', tasks: clientTasks.filter(t => t.due_date && t.due_date.slice(0,10) > nextWeekEnd && t.status !== 'completed') },
                  { id: 'no_date', label: 'No Due Date', color: 'text-muted-foreground/60', tasks: clientTasks.filter(t => !t.due_date && t.status !== 'completed' && t.status !== 'cancelled') },
                  { id: 'completed', label: 'Completed', color: 'text-green-500/70', tasks: clientTasks.filter(t => t.status === 'completed') },
                ];
                const toggleGrp = (id) => setCollapsedGroups(prev => ({ ...prev, [id]: !prev[id] }));
                const isGrpOpen = (id) => {
                  if (collapsedGroups[id] !== undefined) return !collapsedGroups[id];
                  return !['later', 'no_date', 'completed'].includes(id);
                };
                const boardCols = [
                  { status: 'not_started', label: 'Not Started', dot: 'bg-slate-400', bg: 'bg-slate-500/[0.03]', border: 'border-slate-500/20' },
                  { status: 'in_progress', label: 'In Progress', dot: 'bg-gold', bg: 'bg-gold/[0.03]', border: 'border-gold/20' },
                  // BUG-T-001 — Waiting column added between In Progress and Review.
                  { status: 'waiting', label: 'Waiting', dot: 'bg-amber-400', bg: 'bg-amber-500/[0.03]', border: 'border-amber-500/20' },
                  { status: 'review', label: 'Review', dot: 'bg-purple-400', bg: 'bg-purple-500/[0.03]', border: 'border-purple-500/20' },
                  { status: 'completed', label: 'Completed', dot: 'bg-green-500', bg: 'bg-green-500/[0.03]', border: 'border-green-500/20' },
                ];
                const onClientBoardDragEnd = async ({ active, over }) => {
                  if (!over || !active || !canEdit) return;
                  const tid = String(active.id);
                  const newStatus = String(over.id);
                  const cur = clientTasks.find(t => t.id === tid);
                  if (!cur || cur.status === newStatus) return;
                  try { await taskAPI.update(wsId, tid, { status: newStatus }); setClientTasks(prev => prev.map(t => t.id === tid ? { ...t, status: newStatus } : t)); toast.success(`Status → ${newStatus.replace('_', ' ')}`); } catch (err) { toast.error(typeof err?.response?.data?.detail === 'string' ? err.response.data.detail : 'Operation failed'); }
                };
                return (
                  <div>
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-[15px] font-semibold text-foreground">Tasks ({clientTasks.length})</h3>
                      <div className="flex items-center gap-2">
                        <div className="flex bg-foreground/[0.03] rounded-lg p-0.5">
                          <button onClick={() => setTV('list')} data-testid="client-tasks-view-list" className={`px-3 py-1 rounded-md text-[11px] font-medium transition-colors ${taskView === 'list' ? 'bg-gold/15 text-gold' : 'text-muted-foreground/40'}`}>List</button>
                          <button onClick={() => setTV('board')} data-testid="client-tasks-view-board" className={`px-3 py-1 rounded-md text-[11px] font-medium transition-colors ${taskView === 'board' ? 'bg-gold/15 text-gold' : 'text-muted-foreground/40'}`}>Board</button>
                        </div>
                        {canEdit && (
                          <Button
                            size="sm"
                            className="h-8 text-[12px] bg-gold/10 hover:bg-gold/20 text-gold border-0"
                            onClick={() => setShowNewTask(true)}
                            data-testid="client-add-task-btn"
                          >
                            <Plus className="h-3 w-3 mr-1" /> Add Task
                          </Button>
                        )}
                      </div>
                    </div>
                    {taskView === 'list' ? (
                      <div className="space-y-1">
                        {groups.filter(g => g.tasks.length > 0).map(g => (
                          <div key={g.id}>
                            <button onClick={() => toggleGrp(g.id)} className="flex items-center gap-2 w-full py-2 px-1">
                              <ChevronRight className={`h-3 w-3 text-muted-foreground/30 transition-transform ${isGrpOpen(g.id) ? 'rotate-90' : ''}`} />
                              <span className={`text-[12px] font-semibold ${g.color}`}>{g.label}</span>
                              <span className="text-[11px] text-muted-foreground/30">({g.tasks.length})</span>
                            </button>
                            {isGrpOpen(g.id) && <div className="ml-1">{g.tasks.map(t => {
                              const isDone = t.status === 'completed' || t.status === 'cancelled';
                              return (
                                <button key={t.id} onClick={() => setSelectedTaskId(t.id)} className="flex items-center gap-3 w-full px-3 py-2 rounded-lg text-left hover:bg-foreground/[0.02] border border-transparent hover:border-border/20">
                                  {!isDone && <span className={`h-2.5 w-2.5 rounded-full shrink-0 ${t.priority === 'high' ? 'bg-red-500/70' : t.priority === 'low' ? 'bg-foreground/20' : 'bg-gold'}`} />}
                                  <span className="text-[13px] truncate flex-1 text-foreground font-medium">{t.title}</span>
                                  {t.due_date && t.due_date.slice(0,10) < today && !isDone && <span className="text-[8px] px-1.5 py-0.5 rounded bg-red-500/15 text-red-400 font-semibold shrink-0">OVERDUE</span>}
                                  <TaskStatusIndicator status={t.status} size="xs" />
                                  {t.due_date && <span className="text-[11px] text-muted-foreground/40">{formatDate(t.due_date)}</span>}
                                </button>
                              );
                            })}</div>}
                          </div>
                        ))}
                        {clientTasks.length === 0 && <p className="text-[13px] text-muted-foreground/40 py-8 text-center">No tasks linked to this client</p>}
                      </div>
                    ) : (
                      <DndContext sensors={boardSensors} onDragEnd={onClientBoardDragEnd} accessibility={{ announcements: { onDragStart: ({ active }) => `Picked up task ${active.id}`, onDragOver: ({ over }) => over ? `Hovering over ${over.id}` : 'No drop target', onDragEnd: ({ over }) => over ? `Dropped into ${over.id}` : 'Drag cancelled', onDragCancel: () => 'Drag cancelled' } }}>
                      <div className="flex gap-3 overflow-x-auto pb-4" style={{ minHeight: '400px' }}>
                        {boardCols.map(col => {
                          const colTasks = clientTasks.filter(t => t.status === col.status);
                          return (
                            <ClientBoardDroppable key={col.status} id={col.status} className={`flex-1 min-w-[200px] rounded-xl border ${col.border} ${col.bg} flex flex-col`}>
                              <div className={`px-3 py-2.5 border-b border-border/10 flex items-center gap-2`}>
                                <span className={`h-2 w-2 rounded-full ${col.dot}`} />
                                <span className="text-[12px] font-semibold text-foreground">{col.label}</span>
                                <span className="text-[11px] text-muted-foreground/30">({colTasks.length})</span>
                              </div>
                              <div className="flex-1 p-2 space-y-1.5 overflow-y-auto">
                                {colTasks.map(t => {
                                  const isDoneCol = t.status === 'completed' || t.status === 'cancelled';
                                  const isOverdueT = t.due_date && t.due_date.slice(0,10) < today && !isDoneCol;
                                  return (
                                    <ClientBoardDraggable
                                      key={t.id}
                                      id={t.id}
                                      disabled={!canEdit}
                                      onOpen={() => setSelectedTaskId(t.id)}
                                      className="p-2.5 rounded-lg bg-card border border-border/20 cursor-pointer hover:border-border/40 relative"
                                    >
                                      {isOverdueT && <span className="inline-block text-[7px] px-1.5 py-0.5 rounded bg-red-500/15 text-red-400 font-bold mb-1">OVERDUE</span>}
                                      <p className="text-[12px] font-medium text-foreground leading-snug">{t.title}</p>
                                      <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                                        {!isDoneCol && <span className={`h-1.5 w-1.5 rounded-full ${t.priority === 'high' ? 'bg-red-500' : t.priority === 'low' ? 'bg-foreground/20' : 'bg-gold'}`} />}
                                        <TaskStatusIndicator status={t.status} size="xs" />
                                        {t.due_date && <span className="text-[9px] text-muted-foreground/40 ml-auto">{formatDate(t.due_date)}</span>}
                                      </div>
                                    </ClientBoardDraggable>
                                  );
                                })}
                                {colTasks.length === 0 && <p className="text-[11px] text-muted-foreground/20 text-center py-4">Empty</p>}
                              </div>
                            </ClientBoardDroppable>
                          );
                        })}
                      </div>
                      </DndContext>
                    )}
                  </div>
                );
              })()}

              {/* BRAND KIT TAB — Batch 5 R3 CT-013 flagship.
                  Replaces the legacy hex-array + drive-link display with the
                  full BrandKitTab (logo, swatches w/ labels, fonts w/ Google
                  picker + custom upload, guidelines PDF, ZIP download). */}
              {activeTab === 'brand' && (
                <BrandKitTab
                  clientId={selectedClient.id}
                  workspaceId={wsId}
                  canEdit={canEdit}
                />
              )}

              {/* D60 PHASE 1 — Locations tab. Multi-location franchise support. */}
              {activeTab === 'locations' && (
                <ClientLocationsTab
                  workspaceId={wsId}
                  clientId={selectedClient.id}
                  clientName={selectedClient.name}
                />
              )}

              {/* D61 — Content Calendar tab. Per-client only. */}
              {activeTab === 'content' && (
                <ContentCalendar
                  workspaceId={wsId}
                  clientId={selectedClient.id}
                  clients={[selectedClient]}
                />
              )}

              {/* CONTACTS TAB */}
              {activeTab === 'contacts' && (
                <div data-testid="contacts-tab">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-[15px] font-semibold text-foreground">Contacts ({(selectedClient.contacts || []).length})</h3>
                    {/* Batch 2B B8 (CT-016) — Add Contact button, opens inline form. */}
                    {canEdit && (
                      <Button size="sm" className="h-8 text-[12px] bg-gold/10 hover:bg-gold/20 text-gold border-0"
                        data-testid="add-contact-btn"
                        onClick={() => setShowAddContact(true)}>
                        <Plus className="h-3 w-3 mr-1" /> Add Contact
                      </Button>
                    )}
                  </div>
                  {(selectedClient.contacts || []).length === 0 ? (
                    <p className="text-[13px] text-muted-foreground/40 py-8 text-center" data-testid="contacts-empty">
                      No contacts added{canEdit && ' — click "Add Contact" above.'}
                    </p>
                  ) : (
                    <div className="space-y-3">{selectedClient.contacts.map((c, i) => {
                      // Batch 2B B7 (CT-014/015) — tel:/mailto: links + email always visible.
                      const phoneDigits = (c.phone || '').replace(/[^\d+]/g, '');
                      const phoneHref = phoneDigits ? `tel:${phoneDigits.startsWith('+') ? phoneDigits : '+1' + phoneDigits.replace(/^\+/, '')}` : null;
                      return (
                        <Card key={i} className="bg-card/60 border-border/20" data-testid={`contact-card-${i}`}>
                          <CardContent className="p-4 flex items-center gap-4">
                            <div className="h-11 w-11 rounded-full bg-foreground/[0.06] flex items-center justify-center shrink-0">
                              <span className="text-[13px] font-bold text-foreground/70">{(c.name || '?')[0]?.toUpperCase()}</span>
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-[14px] font-semibold text-foreground">{c.name || 'Unnamed'}</p>
                              <div className="flex flex-wrap gap-x-4 gap-y-0.5 text-[12px] mt-0.5">
                                <a
                                  href={c.email ? `mailto:${c.email}` : undefined}
                                  className={c.email ? 'text-gold hover:underline' : 'text-muted-foreground/30 pointer-events-none'}
                                  data-testid={`contact-email-${i}`}
                                >{c.email || '— add email'}</a>
                                {phoneHref ? (
                                  <a href={phoneHref} className="text-gold hover:underline" data-testid={`contact-phone-${i}`}>{c.phone}</a>
                                ) : (
                                  <span className="text-muted-foreground/30" data-testid={`contact-phone-${i}`}>— add phone</span>
                                )}
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      );
                    })}</div>
                  )}
                </div>
              )}

              {/* DELIVERABLES TAB — Batch 3 A5 */}
              {activeTab === 'deliverables' && (
                <div className="space-y-6">
                  <h3 className="text-[15px] font-semibold text-foreground">Monthly Deliverables</h3>
                  {(() => {
                    const counts = deliverableCounts?.counts || {};
                    const cycleStr = deliverableCounts?.cycle
                      ? `Posting cycle: ${formatDate(deliverableCounts.cycle.start)} — ${formatDate(deliverableCounts.cycle.end)}`
                      : (selectedClient.posting_cycle?.start_day ? `Posting cycle: ${ordinal(selectedClient.posting_cycle.start_day)} of each month` : '');
                    const types = Object.entries(counts);
                    const other = deliverableCounts?.other_work || { active: 0, completed_in_cycle: 0 };
                    return (types.length > 0 || (deliverables.posts || deliverables.stories || deliverables.reels)) ? (
                      <>
                        {types.length > 0 ? (
                          <div className="space-y-3">
                            {types.map(([typeName, info]) => {
                              const target = Number(info.target || 0);
                              const done = Number(info.completed || 0);
                              const pct = target > 0 ? Math.min(100, Math.round((done / target) * 100)) : 0;
                              return (
                                <div key={typeName} data-testid={`deliverable-row-${typeName}`}>
                                  <div className="flex items-baseline justify-between mb-1">
                                    <span className="text-[13px] text-foreground/80">{typeName}</span>
                                    <span className="text-[12px] tabular-nums text-muted-foreground/60"><strong className="text-foreground">{done}</strong> / {target}</span>
                                  </div>
                                  <div className="h-2 bg-foreground/[0.05] rounded-full overflow-hidden">
                                    <div className="h-full bg-gold/70 rounded-full transition-all" style={{ width: `${pct}%` }} />
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        ) : (
                          <p className="text-[13px] text-muted-foreground/40">Loading counts…</p>
                        )}
                        {cycleStr && (
                          <p
                            className="text-[12px] text-muted-foreground/60 flex items-center gap-2"
                            data-testid="deliv-cycle-string"
                            data-start-date={deliverableCounts?.cycle?.start || ''}
                            data-end-date={deliverableCounts?.cycle?.end || ''}
                          >
                            <span>{cycleStr}</span>
                            {canEdit && (
                              <button
                                type="button"
                                onClick={() => { openEditDialog(); }}
                                className="text-[10px] text-gold/70 hover:text-gold underline-offset-2 hover:underline"
                                data-testid="deliv-inline-quota-edit"
                                title="Edit quotas inline"
                              >
                                Edit quotas
                              </button>
                            )}
                          </p>
                        )}
                        {/* Batch 3 A5 — Other Work bucket */}
                        <div className="pt-4 border-t border-border/20" data-testid="other-work-section">
                          <h4 className="text-[13px] font-semibold text-foreground/70 mb-2">Other Work</h4>
                          <div className="grid grid-cols-2 gap-3">
                            <div className="rounded-lg border border-border/20 bg-foreground/[0.02] p-3">
                              <p className="text-[10px] text-muted-foreground/50 uppercase tracking-wider">Active</p>
                              <p className="text-xl font-bold text-foreground mt-1" data-testid="other-work-active">{other.active} <span className="text-[12px] font-normal text-muted-foreground/50">tasks</span></p>
                            </div>
                            <div className="rounded-lg border border-border/20 bg-foreground/[0.02] p-3">
                              <p className="text-[10px] text-muted-foreground/50 uppercase tracking-wider">Completed this cycle</p>
                              <p className="text-xl font-bold text-foreground mt-1" data-testid="other-work-completed">{other.completed_in_cycle}</p>
                            </div>
                          </div>
                          {/* Batch 2B B12 (CT-021) — clickable to filter Tasks tab. */}
                          <button
                            type="button"
                            onClick={() => setActiveTabPersist('tasks')}
                            className="text-[11px] text-gold/70 hover:text-gold underline-offset-2 hover:underline mt-2"
                            data-testid="other-work-jump-tasks"
                          >Tasks tagged "None/Other" — billboards, signage, menus, etc. View →</button>
                        </div>
                      </>
                    ) : <p className="text-[13px] text-muted-foreground/40 py-8 text-center">No deliverables set{canEdit && ' — click Edit to configure'}</p>;
                  })()}
                  {/* Batch 2B B11 (CT-022) — Past Cycles section. Hoisted
                      ABOVE the deliverables-counter conditional so it renders
                      on the Deliverables tab even when current quotas are
                      unset; historical performance is a separate concern. */}
                  <PastCyclesSection wsId={wsId} clientId={selectedClient.id} />
                </div>
              )}

              {/* PAGES TAB */}
              {activeTab === 'pages' && (
                <div data-testid="pages-tab">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-[15px] font-semibold text-foreground">Pages ({clientPages.length})</h3>
                    {canEdit && <Button size="sm" className="h-8 text-[12px] bg-gold/10 hover:bg-gold/20 text-gold border-0" onClick={createClientPage} data-testid="new-page-btn"><Plus className="h-3 w-3 mr-1" /> New Page</Button>}
                  </div>
                  {clientPages.length === 0 ? <p className="text-[13px] text-muted-foreground/40 py-8 text-center" data-testid="pages-empty">No pages yet</p> : (
                    <div className="space-y-1">{clientPages.map(p => {
                      // Batch 2B B13 (CT-024) — show last-modified, author, content preview.
                      const updated = p.updated_at || p.created_at || '';
                      const previewRaw = (p.content || '').replace(/<[^>]+>/g, '').replace(/\s+/g, ' ').trim();
                      const preview = previewRaw.slice(0, 100) || 'No content yet';
                      const authorName = p.author_name || p.created_by_name || '';
                      return (
                        <button
                          key={p.id}
                          onClick={async () => {
                            try {
                              // D54 B3 — bug fix: clientPageAPI.get signature
                              // is (workspaceId, clientGroupId, pageId) but
                              // we were passing only 2 args, which silently
                              // 404'd and left activePage null (page row
                              // appeared to only highlight).
                              const res = await clientPageAPI.get(wsId, selectedClient.id, p.id);
                              setActivePage(res.data);
                              setPageTitle(res.data.title || '');
                            } catch { /* ignore */ }
                          }}
                          className="flex items-start gap-3 w-full px-3 py-2.5 rounded-lg text-left hover:bg-foreground/[0.02] border border-transparent hover:border-border/20"
                          data-testid={`page-row-${p.id}`}
                        >
                          <FileText className="h-4 w-4 text-muted-foreground/30 shrink-0 mt-0.5" />
                          <div className="flex-1 min-w-0">
                            <p className="text-[13px] truncate text-foreground font-medium">{p.title || 'Untitled'}</p>
                            <p className="text-[11px] text-muted-foreground/50 truncate mt-0.5">{preview}</p>
                            <p className="text-[10px] text-muted-foreground/40 mt-0.5">
                              {authorName && <span>{authorName} · </span>}
                              {updated ? `updated ${formatDate(updated)}` : ''}
                            </p>
                          </div>
                        </button>
                      );
                    })}</div>
                  )}
                </div>
              )}

              {/* MEDIA TAB (D81 — Google-Drive-style asset library; replaces Files) */}
              {activeTab === 'files' && (
                <div data-testid="files-tab" className="min-h-[500px]">
                  <ClientMediaLibrary
                    workspaceId={wsId}
                    clientId={selectedClient.id}
                    canEdit={canEdit}
                  />
                </div>
              )}

              {/* ACTIVITY TAB (basic) */}
              {activeTab === 'activity' && (
                <ClientActivityFeed wsId={wsId} clientId={selectedClient.id} />
              )}

            </div>
          </ScrollArea>
        </div>

        {/* Edit Client Dialog — rendered IN the detail view (fixes Bug C+D) */}
        <Dialog open={showEditClient} onOpenChange={setShowEditClient}>
          <DialogContent className="sm:max-w-[560px] max-h-[85vh] overflow-y-auto bg-card border-border/40">
            <DialogHeader><DialogTitle className="font-display text-[17px]">Edit Client</DialogTitle></DialogHeader>
            <div className="space-y-5 py-2">
              <div className="space-y-1.5"><Label className="text-[12px]">Business Name *</Label><Input value={newClient.name} onChange={e => setNewClient(p => ({ ...p, name: e.target.value }))} className="h-10 text-[13px] bg-foreground/[0.02] border-border/30" /></div>
              <div className="space-y-1.5"><div className="flex items-center justify-between"><Label className="text-[12px]">Description</Label><AIEnhanceButton text={newClient.description} onEnhanced={t => setNewClient(p => ({ ...p, description: t }))} /></div><AutoGrowTextarea value={newClient.description} onChange={e => setNewClient(p => ({ ...p, description: e.target.value }))} minRows={6} /></div>
              <div className="space-y-2"><div className="flex items-center justify-between"><p className="text-[10px] font-semibold text-muted-foreground/40 uppercase">Contacts</p><Button variant="ghost" size="sm" className="h-6 text-[10px]" onClick={() => setNewClient(p => ({ ...p, contacts: [...p.contacts, { name: '', email: '', phone: '' }] }))}>+ Add</Button></div>
                {newClient.contacts.map((c, i) => <div key={i} className="grid grid-cols-3 gap-2"><Input placeholder="Name" value={c.name} onChange={e => { const cs = [...newClient.contacts]; cs[i].name = e.target.value; setNewClient(p => ({ ...p, contacts: cs })); }} className="h-8 text-[12px] bg-foreground/[0.02] border-border/30" /><Input placeholder="Email" value={c.email} onChange={e => { const cs = [...newClient.contacts]; cs[i].email = e.target.value; setNewClient(p => ({ ...p, contacts: cs })); }} className="h-8 text-[12px] bg-foreground/[0.02] border-border/30" /><Input placeholder="Phone" value={c.phone} onChange={e => { const cs = [...newClient.contacts]; cs[i].phone = e.target.value; setNewClient(p => ({ ...p, contacts: cs })); }} className="h-8 text-[12px] bg-foreground/[0.02] border-border/30" /></div>)}
              </div>
              <div className="grid grid-cols-3 gap-3"><div className="space-y-1"><Label htmlFor="edit-deliv-posts" className="text-[10px]">Posts/mo</Label><Input id="edit-deliv-posts" name="posts_quota" type="number" min="0" value={newClient.deliverables.posts} onChange={e => setNewClient(p => ({ ...p, deliverables: { ...p.deliverables, posts: parseInt(e.target.value) || 0 } }))} className="h-8 text-[12px] bg-foreground/[0.02] border-border/30" placeholder="e.g. 10" data-testid="edit-deliv-posts" /></div><div className="space-y-1"><Label htmlFor="edit-deliv-stories" className="text-[10px]">Stories/mo</Label><Input id="edit-deliv-stories" name="stories_quota" type="number" min="0" value={newClient.deliverables.stories} onChange={e => setNewClient(p => ({ ...p, deliverables: { ...p.deliverables, stories: parseInt(e.target.value) || 0 } }))} className="h-8 text-[12px] bg-foreground/[0.02] border-border/30" placeholder="e.g. 10" data-testid="edit-deliv-stories" /></div><div className="space-y-1"><Label htmlFor="edit-deliv-reels" className="text-[10px]">Reels/mo</Label><Input id="edit-deliv-reels" name="reels_quota" type="number" min="0" value={newClient.deliverables.reels} onChange={e => setNewClient(p => ({ ...p, deliverables: { ...p.deliverables, reels: parseInt(e.target.value) || 0 } }))} className="h-8 text-[12px] bg-foreground/[0.02] border-border/30" placeholder="e.g. 5" data-testid="edit-deliv-reels" /></div></div>
              {/* Batch 3 A1 — Monthly cycle start day */}
              <div className="space-y-1.5">
                <Label htmlFor="edit-cycle-start-day" className="text-[12px]">Monthly Cycle Start Day</Label>
                <div className="flex items-center gap-2">
                  <Input
                    id="edit-cycle-start-day" name="cycle_day"
                    type="number" min="1" max="31"
                    value={newClient.posting_cycle?.start_day ?? 1}
                    onChange={e => {
                      const raw = parseInt(e.target.value) || 1;
                      const sd = Math.max(1, Math.min(31, raw));
                      setNewClient(p => ({ ...p, posting_cycle: { ...(p.posting_cycle || {}), start_day: sd, end_day: sd } }));
                    }}
                    className="h-8 w-20 text-[12px] bg-foreground/[0.02] border-border/30"
                    placeholder="e.g. 1"
                    data-testid="cycle-start-day"
                  />
                  <span className="text-[11px] text-muted-foreground/50">of each month (1-31; clamps to month-end when needed)</span>
                </div>
              </div>
              {/* Batch 3 A2 — Custom deliverable types (max 5) */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <p className="text-[10px] font-semibold text-muted-foreground/40 uppercase">Custom Deliverable Types (Optional)</p>
                  <Button
                    variant="ghost" size="sm" className="h-6 text-[10px]"
                    disabled={(newClient.custom_deliverable_types || []).length >= 5}
                    onClick={() => setNewClient(p => ({ ...p, custom_deliverable_types: [...(p.custom_deliverable_types || []), { type_name: '', monthly_target: 0 }] }))}
                    data-testid="add-custom-type-btn"
                  >
                    + Add Custom Type
                  </Button>
                </div>
                {(newClient.custom_deliverable_types || []).length === 0 && (
                  <p className="text-[11px] text-muted-foreground/30">e.g. Newsletters, Carousels, Billboards…</p>
                )}
                {(newClient.custom_deliverable_types || []).map((ct, i) => (
                  <div key={i} className="grid grid-cols-[1fr_120px_auto] gap-2 items-center" data-testid={`custom-type-row-${i}`}>
                    <Input
                      placeholder="Type name (e.g. Newsletter)"
                      value={ct.type_name || ''}
                      onChange={e => {
                        const arr = [...newClient.custom_deliverable_types]; arr[i] = { ...arr[i], type_name: e.target.value };
                        setNewClient(p => ({ ...p, custom_deliverable_types: arr }));
                      }}
                      className="h-8 text-[12px] bg-foreground/[0.02] border-border/30"
                    />
                    <Input
                      type="number" min="0" placeholder="Monthly target"
                      value={ct.monthly_target ?? 0}
                      onChange={e => {
                        const arr = [...newClient.custom_deliverable_types]; arr[i] = { ...arr[i], monthly_target: Math.max(0, parseInt(e.target.value) || 0) };
                        setNewClient(p => ({ ...p, custom_deliverable_types: arr }));
                      }}
                      className="h-8 text-[12px] bg-foreground/[0.02] border-border/30"
                    />
                    <button
                      type="button"
                      onClick={() => setNewClient(p => ({ ...p, custom_deliverable_types: p.custom_deliverable_types.filter((_, idx) => idx !== i) }))}
                      className="p-1 text-muted-foreground/30 hover:text-red-400"
                      title="Remove"
                      data-testid={`custom-type-remove-${i}`}
                    ><Trash2 className="h-3 w-3" /></button>
                  </div>
                ))}
              </div>
              <div className="space-y-1.5"><Label className="text-[12px]">Drive Link</Label><Input value={newClient.brand_drive_link} onChange={e => setNewClient(p => ({ ...p, brand_drive_link: e.target.value }))} className="h-8 text-[12px] bg-foreground/[0.02] border-border/30" placeholder="https://drive.google.com/..." /></div>
              <div className="space-y-1.5"><div className="flex items-center justify-between"><Label className="text-[10px]">Brand Colors</Label><Button variant="ghost" size="sm" className="h-5 text-[9px]" onClick={() => setNewClient(p => ({ ...p, brand_colors: [...p.brand_colors, ''] }))}>+</Button></div><div className="flex flex-wrap gap-2">{newClient.brand_colors.map((c, i) => <div key={i} className="flex items-center gap-1"><Input placeholder="#hex" value={c} onChange={e => { const cols = [...newClient.brand_colors]; cols[i] = e.target.value; setNewClient(p => ({ ...p, brand_colors: cols })); }} className="h-7 w-20 text-[11px] bg-foreground/[0.02] border-border/30" />{c?.startsWith('#') && <div className="h-5 w-5 rounded border border-border/30" style={{ backgroundColor: c }} />}</div>)}</div></div>
            </div>
            <DialogFooter><Button variant="ghost" onClick={() => setShowEditClient(false)}>Cancel</Button><Button onClick={handleSaveEdit} className="bg-gold/10 hover:bg-gold/20 text-gold border-0" data-testid="save-edit-btn">Save Changes</Button></DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Batch 2B B8 (CT-016) — Add Contact dialog. */}
        <Dialog open={showAddContact} onOpenChange={setShowAddContact}>
          <DialogContent className="sm:max-w-[440px] bg-card border-border/40" data-testid="add-contact-dialog">
            <DialogHeader>
              <DialogTitle className="font-display text-[17px]">Add Contact</DialogTitle>
              <DialogDescription className="text-[12px] text-muted-foreground/60">Add a new contact for {selectedClient?.name}.</DialogDescription>
            </DialogHeader>
            <div className="space-y-3 py-2">
              <div className="space-y-1.5">
                <Label htmlFor="new-contact-name" className="text-[12px]">Name *</Label>
                <Input id="new-contact-name" name="contact_name" value={newContact.name}
                  onChange={e => setNewContact(p => ({ ...p, name: e.target.value }))}
                  className="h-9 text-[13px] bg-foreground/[0.02] border-border/30" placeholder="e.g. Jane Doe" data-testid="new-contact-name" />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="new-contact-email" className="text-[12px]">Email</Label>
                <Input id="new-contact-email" name="contact_email" type="email" value={newContact.email}
                  onChange={e => setNewContact(p => ({ ...p, email: e.target.value }))}
                  className="h-9 text-[13px] bg-foreground/[0.02] border-border/30" placeholder="jane@example.com" data-testid="new-contact-email" />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="new-contact-phone" className="text-[12px]">Phone</Label>
                <Input id="new-contact-phone" name="contact_phone" type="tel" value={newContact.phone}
                  onChange={e => setNewContact(p => ({ ...p, phone: e.target.value }))}
                  className="h-9 text-[13px] bg-foreground/[0.02] border-border/30" placeholder="+1 (555) 123-4567" data-testid="new-contact-phone" />
              </div>
              {/* RF-A3 — Role dropdown. */}
              <div className="space-y-1.5">
                <Label htmlFor="new-contact-role" className="text-[12px]">Role</Label>
                <select
                  id="new-contact-role" name="contact_role" value={newContact.role}
                  onChange={e => setNewContact(p => ({ ...p, role: e.target.value }))}
                  className="w-full h-9 text-[13px] bg-foreground/[0.02] border border-border/30 rounded-md px-2 outline-none"
                  data-testid="new-contact-role"
                >
                  <option value="Primary Contact">Primary Contact</option>
                  <option value="Billing">Billing</option>
                  <option value="Marketing">Marketing</option>
                  <option value="Decision Maker">Decision Maker</option>
                  <option value="Other">Other</option>
                </select>
              </div>
            </div>
            <DialogFooter>
              <Button variant="ghost" onClick={() => setShowAddContact(false)}>Cancel</Button>
              <Button
                onClick={async () => {
                  if (!newContact.name.trim()) { toast.error('Name is required'); return; }
                  try {
                    const next = [...(selectedClient.contacts || []), newContact];
                    const res = await clientGroupAPI.update(wsId, selectedClient.id, { contacts: next });
                    setSelectedClient(res.data);
                    setClients(prev => prev.map(c => c.id === selectedClient.id ? res.data : c));
                    setNewContact({ name: '', email: '', phone: '', role: 'Primary Contact' });
                    setShowAddContact(false);
                    toast.success('Contact added');
                  } catch (err) {
                    toast.error(err?.response?.data?.detail || 'Failed');
                  }
                }}
                className="bg-gold/10 hover:bg-gold/20 text-gold border-0"
                data-testid="save-contact-btn"
              >Add Contact</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Batch 1 ITEM 7 (BUG-RW-T-012) — kanban out-of-sync after status
            change in the side pane. Pass an onUpdate callback so the
            client task list re-fetches the moment any mutation in the
            sheet completes (status, assignees, due date, deletion, …). */}
        <TaskDetailSheet
          taskId={selectedTaskId}
          open={!!selectedTaskId}
          onOpenChange={o => { if (!o) setSelectedTaskId(null); }}
          onUpdate={() => {
            if (selectedClient && wsId) {
              taskAPI.list(wsId, { client_group_id: selectedClient.id })
                .then(res => setClientTasks(res.data || []))
                .catch(() => {});
            }
          }}
        />

        {/* BUG-C05 — Delete-Client confirm dialog (cascade-with-counts).
            Renders only while the client detail page is open; the kebab
            "Delete Client" item triggers openDeleteDialog(). */}
        <Dialog open={!!deleteDialog} onOpenChange={(open) => { if (!open && !deletingClient) setDeleteDialog(null); }}>
          <DialogContent className="sm:max-w-[440px] bg-card border-border/40" data-testid="delete-client-dialog">
            <DialogHeader>
              <DialogTitle className="font-display text-[17px] text-destructive">Delete Client</DialogTitle>
              <DialogDescription className="text-[12px] text-muted-foreground/60">
                This will soft-delete the client. You'll have 5 seconds to undo from the toast.
              </DialogDescription>
            </DialogHeader>
            {deleteDialog?.loading ? (
              <div className="flex items-center justify-center py-6 text-[12px] text-muted-foreground/50">
                <Loader2 className="h-4 w-4 animate-spin mr-2" /> Calculating impact…
              </div>
            ) : deleteDialog?.previewError ? (
              // BUG-C05 hardening — preview endpoint failed (e.g. 502).
              // Render the dialog body anyway so the user can still proceed;
              // the actual DELETE call will surface its own error if it
              // also fails. Cascade counts are unknown in this state.
              <div className="space-y-3 py-2 text-[13px]" data-testid="delete-client-preview-error">
                <p className="text-foreground">
                  Delete <strong className="text-gold">"{deleteDialog.client?.name}"</strong>?
                </p>
                <div className="rounded-lg border border-amber-500/30 bg-amber-500/[0.04] px-3 py-2 text-[11px] text-amber-300/90">
                  Couldn't load cascade preview ({deleteDialog.previewError}). You can still attempt the delete — any error will be shown after.
                </div>
              </div>
            ) : deleteDialog?.counts ? (
              <div className="space-y-3 py-2 text-[13px]">
                <p className="text-foreground">
                  Delete <strong className="text-gold">"{deleteDialog.counts.client_name}"</strong>?
                </p>
                <div className="rounded-lg border border-border/30 bg-foreground/[0.02] divide-y divide-border/20" data-testid="delete-client-counts">
                  <div className="flex items-center justify-between px-3 py-2">
                    <span className="text-muted-foreground">Linked tasks</span>
                    <span className="font-medium tabular-nums">{deleteDialog.counts.tasks}</span>
                  </div>
                  <div className="flex items-center justify-between px-3 py-2">
                    <span className="text-muted-foreground">Submissions on those tasks</span>
                    <span className="font-medium tabular-nums">{deleteDialog.counts.submissions}</span>
                  </div>
                  <div className="flex items-center justify-between px-3 py-2">
                    <span className="text-muted-foreground">Client pages</span>
                    <span className="font-medium tabular-nums">{deleteDialog.counts.pages}</span>
                  </div>
                  <div className="flex items-center justify-between px-3 py-2">
                    <span className="text-muted-foreground">Client files</span>
                    <span className="font-medium tabular-nums">{deleteDialog.counts.files}</span>
                  </div>
                </div>
                <p className="text-[11px] text-muted-foreground/50 leading-relaxed">
                  Tasks remain linked to the (now hidden) client so an Undo within 5s restores them fully.
                </p>
              </div>
            ) : null}
            <DialogFooter>
              <Button variant="ghost" onClick={() => setDeleteDialog(null)} disabled={deletingClient} data-testid="delete-client-cancel">Cancel</Button>
              <Button
                onClick={confirmDeleteClient}
                disabled={deletingClient || deleteDialog?.loading}
                className="bg-red-500/10 hover:bg-red-500/20 text-red-400 border-0"
                data-testid="delete-client-confirm"
              >
                {deletingClient ? <><Loader2 className="h-3.5 w-3.5 mr-2 animate-spin" />Deleting…</> : <><Trash2 className="h-3.5 w-3.5 mr-2" />Delete client</>}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Iter 62 Bug #2 / #8 / #9 — Client-scoped New Task modal.
            Client field is pre-filled (and shown as a pill); assignees default
            to the current user but the multi-select chip lets the user add
            more assignees inline. */}
        {/* Batch 3 B8 — Unified TaskCreateModal with prefilled client */}
        <TaskCreateModal
          open={showNewTask}
          onOpenChange={setShowNewTask}
          workspaceId={wsId}
          members={members}
          clients={clients}
          prefilledClientId={selectedClient?.id}
          currentUserId={user?.id}
          testIdPrefix="client-new-task"
          onCreated={async () => {
            if (selectedClient) {
              const r = await taskAPI.list(wsId, { client_group_id: selectedClient.id });
              setClientTasks(r.data || []);
              clientGroupAPI.deliverableCounts(wsId, selectedClient.id).then(res => setDeliverableCounts(res.data)).catch(() => {});
            }
          }}
        />
      </div>
    );
  }

  // ============ CLIENT LIST PAGE ============
  return (
    <div className={`${pipelineView === 'board' ? 'max-w-[1600px]' : 'max-w-4xl'} mx-auto p-4 md:p-8 space-y-4`} data-testid="my-clients-page">
      {/* Batch 1 ITEM 4 — friendly banner when the URL points to a client
          the user can't see (deleted, no access, typo'd UUID). Avoids the
          confusing "I refreshed and lost my work" UX. */}
      {invalidClientId && (
        <div className="rounded-lg border border-amber-500/30 bg-amber-500/[0.04] px-4 py-3 flex items-center justify-between" data-testid="invalid-client-banner">
          <div className="text-[13px]">
            <p className="text-amber-300 font-medium">Client not found</p>
            <p className="text-amber-300/70 text-[11px] mt-0.5">You don't have access to this client, or it has been deleted. URL was <code className="font-mono text-[10px]">?client={invalidClientId?.slice(0, 8)}…</code></p>
          </div>
          <Button size="sm" variant="ghost" className="h-7 text-[11px]" onClick={() => { setInvalidClientId(null); setSearchParams({}, { replace: true }); }} data-testid="invalid-client-back-btn">
            <ArrowLeft className="h-3 w-3 mr-1" /> Back to My Clients
          </Button>
        </div>
      )}
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-display font-bold">My Clients</h1>
        {isOwnerOrAdmin && <Button size="sm" onClick={() => setShowAddClient(true)} data-testid="add-client-btn"><Plus className="h-3.5 w-3.5 mr-1.5" /> Add Client</Button>}
      </div>
      <div className="flex items-center gap-3">
        <div className="flex gap-1.5">
          <button onClick={() => setClientFilter('all')} className={`px-3 py-1.5 rounded-lg text-[12px] font-medium ${clientFilter === 'all' ? 'bg-gold/15 text-gold' : 'text-muted-foreground/40'}`}>All Clients</button>
          {!isTeam && <button onClick={() => setClientFilter('mine')} className={`px-3 py-1.5 rounded-lg text-[12px] font-medium ${clientFilter === 'mine' ? 'bg-gold/15 text-gold' : 'text-muted-foreground/40'}`} data-testid="client-filter-mine">Clients I Manage</button>}
          {/* Batch 2A CT-008/009 — view archived clients */}
          {isOwnerOrAdmin && <button onClick={() => setClientFilter('archived')} className={`px-3 py-1.5 rounded-lg text-[12px] font-medium ${clientFilter === 'archived' ? 'bg-gold/15 text-gold' : 'text-muted-foreground/40'}`} data-testid="client-filter-archived">Archived</button>}
        </div>
        {/* Batch 5 R2 F1 — Hybrid By-Client Visibility replaces the Batch 2
            "Mine" tab fallback. Server now filters via account_managers +
            supervisor tree; no more "showing all" stand-in note. */}
        <div className="flex-1 flex items-center gap-2 h-8 px-3 rounded-lg border border-border/20 bg-foreground/[0.02] focus-within:border-gold/30 ml-auto max-w-xs">
          <Search className="h-3.5 w-3.5 text-muted-foreground/30" />
          <input value={searchQuery} onChange={e => setSearchQuery(e.target.value)} placeholder="Search clients..." className="bg-transparent border-none outline-none text-[12px] flex-1" />
        </div>
        {/* D83 — Board/List view toggle. Survives reload via localStorage. */}
        <div className="flex items-center border border-border/30 rounded-lg overflow-hidden" data-testid="d83-view-toggle">
          <button
            onClick={() => togglePipelineView('list')}
            className={`px-2.5 py-1.5 text-[12px] flex items-center gap-1 ${pipelineView === 'list' ? 'bg-gold/15 text-gold' : 'text-muted-foreground/50 hover:text-foreground'}`}
            data-testid="d83-view-list"
            aria-pressed={pipelineView === 'list'}
            title="List view"
          >
            <ListIcon className="h-3.5 w-3.5" /> List
          </button>
          <button
            onClick={() => togglePipelineView('board')}
            className={`px-2.5 py-1.5 text-[12px] flex items-center gap-1 ${pipelineView === 'board' ? 'bg-gold/15 text-gold' : 'text-muted-foreground/50 hover:text-foreground'}`}
            data-testid="d83-view-board"
            aria-pressed={pipelineView === 'board'}
            title="Board view"
          >
            <LayoutGrid className="h-3.5 w-3.5" /> Board
          </button>
        </div>
      </div>
      {loading ? [1, 2, 3].map(i => <div key={i} className="h-16 bg-secondary/10 rounded-xl animate-pulse" />) : filteredClients.length === 0 ? (
        // Batch 2B C15 (CT-005) — explicit empty-state when search returns 0
        // results; aria-live so SR users get the count update.
        searchQuery ? (
          <div className="text-center py-16" role="status" aria-live="polite" data-testid="client-search-empty">
            <Building2 className="h-12 w-12 text-muted-foreground/10 mx-auto mb-3" />
            <p className="text-muted-foreground/60 text-[13px]">No clients match "{searchQuery}".</p>
            <button onClick={() => setSearchQuery('')} className="text-[12px] text-gold hover:underline mt-2" data-testid="client-search-clear">Clear search</button>
          </div>
        ) : (
          <div className="text-center py-16" data-testid="client-list-empty">
            <Building2 className="h-12 w-12 text-muted-foreground/10 mx-auto mb-3" />
            <p className="text-muted-foreground/40 text-[13px]">{clientFilter === 'mine' ? 'No clients assigned to you' : clientFilter === 'archived' ? 'No archived clients' : 'No clients yet'}</p>
          </div>
        )
      ) : pipelineView === 'board' ? (
        // D83 — Board (Kanban) view. Four columns, drag a card between
        // columns to change status; drop on a specific card to insert
        // ABOVE that card (computes fractional pipeline_order); drop on
        // empty column space to append to the bottom.
        <DndContext sensors={boardSensors} onDragEnd={handleBoardDragEnd}>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3" data-testid="d83-board">
            {D83_STATUSES.map(col => {
              const cards = clientsByStatus[col.id] || [];
              return (
                <ClientBoardDroppable
                  key={col.id}
                  id={col.id}
                  className="rounded-xl border border-border/25 bg-card/40 p-3 min-h-[420px] flex flex-col"
                >
                  <div className="flex items-center justify-between mb-3 px-1">
                    <div className="flex items-center gap-2">
                      <span className={`h-2 w-2 rounded-full ${col.dotClass}`} />
                      <h2 className={`text-[12px] font-semibold uppercase tracking-wide ${col.colorClass}`}>
                        {col.label}
                      </h2>
                    </div>
                    <span
                      className="text-[11px] tabular-nums text-muted-foreground/60 px-1.5 py-0.5 rounded-md bg-foreground/[0.04]"
                      data-testid={`d83-column-count-${col.id}`}
                    >
                      {cards.length}
                    </span>
                  </div>
                  <div className="flex-1 space-y-2 min-h-[40px]">
                    {cards.length === 0 ? (
                      <p className="text-center text-[11px] text-muted-foreground/30 py-6 select-none" data-testid={`d83-column-empty-${col.id}`}>
                        Drop a client here
                      </p>
                    ) : cards.map(c => {
                      const mgrs = (c.account_managers || []).map(id => members.find(m => m.id === id)).filter(Boolean);
                      const cLogo = logoUrls[c.id];
                      return (
                        <ClientBoardDraggable
                          key={c.id}
                          id={c.id}
                          disabled={!canEdit && !isOwnerOrAdmin && !((c.account_managers || []).includes(user?.id))}
                          onOpen={() => openClient(c)}
                          className="group p-2.5 rounded-lg bg-card border border-border/30 hover:border-gold/40 cursor-grab active:cursor-grabbing transition-colors"
                        >
                          <div className="flex items-center gap-2">
                            <GripVertical className="h-3.5 w-3.5 text-muted-foreground/20 group-hover:text-muted-foreground/60 shrink-0" />
                            {cLogo ? (
                              <img src={cLogo} alt="" className="h-7 w-7 rounded-md object-cover shrink-0" />
                            ) : (
                              <div className="h-7 w-7 rounded-md bg-gold/10 flex items-center justify-center shrink-0">
                                <span className="text-[11px] font-bold text-gold">{(c.name || '?')[0]?.toUpperCase()}</span>
                              </div>
                            )}
                            <div className="flex-1 min-w-0">
                              <p className="text-[12.5px] font-semibold truncate" title={c.name}>{c.name}</p>
                              {mgrs.length > 0 && (
                                <p className="text-[10px] text-muted-foreground/40 truncate">
                                  {mgrs.map(m => m.name.split(' ')[0]).join(', ')}
                                </p>
                              )}
                            </div>
                          </div>
                        </ClientBoardDraggable>
                      );
                    })}
                  </div>
                </ClientBoardDroppable>
              );
            })}
          </div>
        </DndContext>
      ) : (
        // 2026-02 — Grouped list view: section headers per status (Ongoing,
        // Potential, Seasonal, Archived) with A→Z sort within each group.
        // Zero-count groups are hidden. Applies inside every tab (All,
        // Clients I Manage, Archived) — the user-side filtering happens in
        // `filteredClients`; we just bucket+render the survivors here.
        <div className="space-y-5" data-testid="d83-list">
          {D83_STATUSES.map(s => {
            const items = clientsByStatus[s.id] || [];
            if (items.length === 0) return null;
            return (
              <div key={s.id} data-testid={`d83-list-group-${s.id}`}>
                <div
                  className="flex items-center gap-2 px-1 py-1.5 mb-1 text-[11px] uppercase tracking-wide"
                  data-testid={`d83-list-group-header-${s.id}`}
                >
                  <span className={`h-1.5 w-1.5 rounded-full ${s.dotClass}`} aria-hidden="true" />
                  <span className={`${s.colorClass} font-semibold`}>{s.label}</span>
                  <span className="text-muted-foreground/40" data-testid={`d83-list-group-count-${s.id}`}>· {items.length}</span>
                </div>
                <div className="space-y-1">{items.map(c => {
          const mgrs = (c.account_managers || []).map(id => members.find(m => m.id === id)).filter(Boolean);
          const cLogo = logoUrls[c.id];
          const cStatus = D83_STATUS_BY_ID[d83NormalizeStatus(c.status)];
          const canEditThis = isOwnerOrAdmin || (c.account_managers || []).includes(user?.id);
          return (
            <div
              key={c.id}
              className="flex items-center gap-3 w-full p-3 rounded-lg card-hover-gold bg-card group"
              data-testid={`client-${c.id}`}
            >
              {/* Click target — the name+logo block opens the client. Status
                  pill is a sibling so it doesn't navigate when clicked. */}
              <button
                onClick={() => openClient(c)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openClient(c); }
                }}
                aria-label={`Open client ${c.name}${mgrs.length > 0 ? ', managed by ' + mgrs.map(m => m.name).join(', ') : ''}`}
                className="flex items-center gap-4 flex-1 min-w-0 text-left focus:outline-none focus:ring-2 focus:ring-gold/40 rounded-lg"
                data-testid={`client-row-${c.id}`}
              >
                {cLogo ? <img src={cLogo} alt="" className="h-9 w-9 rounded-lg object-cover shrink-0" /> : <div className="h-9 w-9 rounded-lg bg-gold/10 flex items-center justify-center shrink-0"><span className="text-sm font-bold text-gold">{(c.name || '?')[0]?.toUpperCase()}</span></div>}
                <div className="flex-1 min-w-0"><p className="text-[13px] font-semibold">{c.name}{c.archived_at && <span className="ml-2 text-[10px] text-amber-300/70">(archived)</span>}{locationCounts[c.id] > 0 && <span className="ml-2 text-[10px] text-gold/80" data-testid={`client-locations-badge-${c.id}`}>({locationCounts[c.id]} location{locationCounts[c.id] === 1 ? '' : 's'})</span>}</p><p className="text-[11px] text-muted-foreground/40">{mgrs.length > 0 ? `Managed by ${mgrs.map(m => m.name).join(', ')}` : ''}</p></div>
              </button>
              {/* D83 — Inline status dropdown. Editable by owner/admin OR
                  this client's account manager. Read-only badge otherwise. */}
              {canEditThis ? (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button
                      className={`px-2.5 py-1 rounded-md border text-[11px] font-medium flex items-center gap-1.5 shrink-0 ${cStatus.badgeClass} hover:opacity-90`}
                      onClick={(e) => e.stopPropagation()}
                      data-testid={`d83-status-trigger-${c.id}`}
                      title="Change status"
                    >
                      <span className={`h-1.5 w-1.5 rounded-full ${cStatus.dotClass}`} />
                      {cStatus.label}
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="bg-card border-border/40">
                    {D83_STATUSES.map(s => (
                      <DropdownMenuItem
                        key={s.id}
                        onClick={(e) => { e.stopPropagation(); applyPipelineUpdate(c.id, s.id); }}
                        data-testid={`d83-status-option-${c.id}-${s.id}`}
                      >
                        <span className={`h-2 w-2 rounded-full mr-2 ${s.dotClass}`} />
                        <span className="flex-1">{s.label}</span>
                        {d83NormalizeStatus(c.status) === s.id && <CheckCircle2 className="h-3 w-3 text-gold ml-1.5" />}
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>
              ) : (
                <span
                  className={`px-2.5 py-1 rounded-md border text-[11px] font-medium flex items-center gap-1.5 shrink-0 ${cStatus.badgeClass}`}
                  data-testid={`d83-status-readonly-${c.id}`}
                >
                  <span className={`h-1.5 w-1.5 rounded-full ${cStatus.dotClass}`} />
                  {cStatus.label}
                </span>
              )}
              <ChevronRight className="h-4 w-4 text-muted-foreground/15 group-hover:text-muted-foreground/40 shrink-0" />
            </div>
          );
        })}</div>
              </div>
            );
          })}
        </div>
      )}

      {/* Add Client Dialog */}
      <Dialog open={showAddClient} onOpenChange={setShowAddClient}>
        <DialogContent className="sm:max-w-[560px] max-h-[85vh] overflow-y-auto bg-card border-border/40">
          <DialogHeader><DialogTitle className="font-display text-[17px]">Add New Client</DialogTitle><DialogDescription className="text-[12px] text-muted-foreground/50">Business name is required. All other fields are optional.</DialogDescription></DialogHeader>
          <div className="space-y-4 py-2">
            {/* Batch 2B B3 (CT-002) — name attribute, required, htmlFor binding. */}
            <div className="space-y-1.5">
              <Label htmlFor="new-client-name" className="text-[12px]">Business Name <span className="text-red-400">*</span></Label>
              <Input
                id="new-client-name" name="business_name"
                placeholder="e.g. Stellar Productions"
                value={newClient.name}
                onChange={e => setNewClient(p => ({ ...p, name: e.target.value }))}
                className="h-10 text-[13px] bg-foreground/[0.02] border-border/30"
                required
                aria-required="true"
                data-testid="new-client-name"
              />
              {!newClient.name.trim() && (
                <p className="text-[11px] text-amber-300/70" data-testid="new-client-name-hint">Business Name is required.</p>
              )}
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="new-client-description" className="text-[12px]">Description</Label>
              <AutoGrowTextarea id="new-client-description" name="description" value={newClient.description}
                onChange={e => setNewClient(p => ({ ...p, description: e.target.value }))}
                minRows={6}
                placeholder="About the business..." />
            </div>
            {/* Batch 2B B3 (CT-003) — explanatory inline note covering post-creation flow. */}
            <p className="text-[11px] text-muted-foreground/50 italic" data-testid="post-create-hint">
              You can add contacts, deliverable quotas, brand colors, and more after saving.
            </p>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowAddClient(false)}>Cancel</Button>
            <Button onClick={handleAddClient} disabled={addingClient || !newClient.name.trim()}
              className="bg-gold/10 hover:bg-gold/20 text-gold border-0 disabled:opacity-50"
              data-testid="submit-client-btn">
              {addingClient ? 'Adding...' : 'Add Client'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}



// ──────────────────────────────────────────────────────────────────────
// Batch 2A CT-026 — Per-client activity feed component.
// Pulls activity_events whose metadata.client_id matches the open client.
// Forward-logging only: events created before this feature shipped won't
// appear (the backend skips them when metadata.client_id is absent).
// ──────────────────────────────────────────────────────────────────────
function ClientActivityFeed({ wsId, clientId }) {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    if (!wsId || !clientId) return;
    setLoading(true);
    clientGroupAPI.activity(wsId, clientId, 100)
      .then(res => setEvents(res.data?.events || []))
      .catch(() => setEvents([]))
      .finally(() => setLoading(false));
  }, [wsId, clientId]);

  const labelFor = (e) => {
    const m = e.metadata || {};
    switch (e.event_type) {
      case 'client_created':   return 'created this client';
      case 'client_edited':    return `edited ${(m.fields_changed || []).slice(0, 3).join(', ') || 'client details'}`;
      case 'client_archived':  return 'archived this client';
      case 'client_unarchived':return 'unarchived this client';
      case 'client_deleted':   return 'deleted this client';
      case 'client_restored':  return 'restored this client';
      case 'client_manager_changed': return 'changed account manager(s)';
      case 'task_created':     return `created task "${m.task_title || ''}"`;
      case 'task_completed':   return 'completed a task';
      case 'task_started':     return 'started a task';
      case 'task_paused':      return 'paused a task';
      case 'task_reopened':    return 'reopened a task';
      case 'status_change':    return `changed status from ${m.from || '?'} to ${m.to || '?'}`;
      case 'task_field_edit':  return `updated ${m.field || 'task'}`;
      case 'submission_approved': return 'approved a submission';
      case 'submission_changes_requested': return 'requested changes on a submission';
      case 'submission_rejected': return 'rejected a submission';
      case 'file_upload':      return `uploaded "${m.filename || 'file'}"`;
      default:                 return e.event_type?.replace(/_/g, ' ') || 'activity';
    }
  };

  return (
    <div data-testid="client-activity-feed">
      <h3 className="text-[15px] font-semibold text-foreground mb-4">Activity</h3>
      {loading ? (
        <div className="space-y-2">
          {[1, 2, 3].map(i => <div key={i} className="h-12 bg-secondary/10 rounded-lg animate-pulse" />)}
        </div>
      ) : events.length === 0 ? (
        <p className="text-[13px] text-muted-foreground/40 py-8 text-center">No activity yet. Actions on tasks and client details will appear here.</p>
      ) : (
        <ul className="space-y-1 max-h-[60vh] overflow-y-auto pr-1">
          {events.map(e => (
            <li key={e.id} className="flex items-start gap-3 py-2 border-b border-border/10" data-testid={`activity-event-${e.event_type}`}>
              <div className="h-7 w-7 rounded-full bg-gold/10 flex items-center justify-center shrink-0">
                <span className="text-[10px] font-bold text-gold">{(e.user?.name || '?')[0]?.toUpperCase()}</span>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-[13px] text-foreground">
                  <span className="font-semibold">{e.user?.name || 'Someone'}</span>{' '}
                  <span className="text-muted-foreground">{labelFor(e)}</span>
                </p>
                <p className="text-[11px] text-muted-foreground/40 mt-0.5">{formatDate(e.created_at)} · {new Date(e.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</p>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────
// Batch 2B B11 (CT-022) — Past Cycles section.
// Renders a collapsible list of the last `limit` posting cycles for this
// client, each showing per-deliverable actuals vs quota and a completion
// rate. Defaults to 6, "Load more" steps by 6 (cap server-side at 24).
// ──────────────────────────────────────────────────────────────────────
function PastCyclesSection({ wsId, clientId }) {
  const [open, setOpen] = useState(false);
  const [limit, setLimit] = useState(6);
  const [data, setData] = useState({ cycles: [] });
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    if (!open || !wsId || !clientId) return;
    setLoading(true);
    clientGroupAPI.pastCycles(wsId, clientId, limit)
      .then(res => setData(res.data || { cycles: [] }))
      .catch(() => setData({ cycles: [] }))
      .finally(() => setLoading(false));
  }, [open, wsId, clientId, limit]);

  return (
    <div className="mt-6 border-t border-border/10 pt-4" data-testid="past-cycles-section">
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="flex items-center gap-2 text-[13px] text-foreground/80 hover:text-gold transition-colors"
        data-testid="past-cycles-toggle"
        aria-expanded={open}
      >
        <ChevronRight className={`h-3.5 w-3.5 transition-transform ${open ? 'rotate-90' : ''}`} />
        <span className="font-medium">Past Cycles</span>
        <span className="text-[11px] text-muted-foreground/40">(historical performance)</span>
      </button>
      {open && (
        <div className="mt-3 space-y-2" data-testid="past-cycles-list">
          {loading && data.cycles.length === 0 ? (
            <div className="space-y-2">
              {[1, 2, 3].map(i => <div key={i} className="h-16 bg-secondary/10 rounded-lg animate-pulse" />)}
            </div>
          ) : data.cycles.length === 0 ? (
            <p className="text-[12px] text-muted-foreground/40 py-4">No past cycles yet.</p>
          ) : (
            <>
              {data.cycles.map(cyc => (
                <Card key={cyc.cycle_start} className="bg-card/40 border-border/20" data-testid={`past-cycle-${cyc.cycle_start}`} data-start-date={cyc.cycle_start} data-end-date={cyc.cycle_end}>
                  <CardContent className="p-3">
                    <div className="flex items-center justify-between mb-1.5">
                      <p className="text-[12px] font-semibold text-foreground">
                        {formatDate(cyc.cycle_start)} — {formatDate(cyc.cycle_end)}
                      </p>
                      <span className="text-[11px] font-mono text-gold/80">
                        {cyc.total_done}/{cyc.total_quota} · {cyc.completion_rate}%
                      </span>
                    </div>
                    <div className="flex flex-wrap gap-x-3 gap-y-0.5 text-[11px]">
                      {cyc.counts.map(c => (
                        <span key={c.type} className="text-muted-foreground/70">
                          <span className="text-foreground/80 font-medium">{c.type}:</span> {c.actual}/{c.quota}
                        </span>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              ))}
              {data.cycles.length >= limit && limit < 24 && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setLimit(l => Math.min(24, l + 6))}
                  className="text-[12px] text-gold w-full"
                  data-testid="past-cycles-load-more"
                >Load more</Button>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}

